import { metricLabel } from '../../components/agent-common/diagnosisLabels';
import type { StateCreator } from 'zustand';
import type { VoyagerState } from '../useVoyagerStore';
import { getAllPerspectives, getDomains, runDiagnose, runDiagnoseForClaim, trackDiagnosis, type DiagnosisRunOptions, type DiagnosisRun, type DiagnoseEnvelope, type ThreadContext, type Attachment } from '../../apis';
import {
  authHeaders, getClerkToken, withWsAuth, WS_AUTH_REJECTED_CODE,
} from '../../auth/clerk';
import { generateId } from '../../utils';
import { layerCorrection } from '../../components/Ladder/corrections';
import { layerContribution } from '../../components/Ladder/contributions';
import { redactClaimWords } from '../../components/Agents/redact';
import type { Perspective, DataDomain } from '../../types';
import type { DefinitionRequest } from './definitionsSlice';
import type { DefaultsBlock } from '../../components/Ladder/claims';
import { reduceAnswerDelta, stoppedWordsOf, streamingAfterFrame, withoutStreaming, type AnswerStreaming, type StoppedWords } from './answerStream';
import { PART, project, publicSnapshot } from './acceptance';
import { liveProgressAfter, PENDING_ACCEPTANCE, plannedOf, type LiveProgress } from './progress';
import { entitySnapshot, type EntityDisclosureSnapshot } from '../../components/LandingAgent/entityShowing';

// The transcript sent with the next question is written into that question's trace on the server: a claim's words
// (#296) never ride it. The reply the history keeps is the headline with every claim title the answer carried
// reduced to its reference — the live card still shows the words while the viewer's access lasts.
export const historyText = (text: string, result: any): string => (redactClaimWords({ text, result }) as any).text || '';

function placeHistoryReply(history: HistoryTurn[], turnId: string, content: string, appendIfUnplaced: boolean): HistoryTurn[] {
  if (!content) return history;
  const reply = { role: 'assistant' as const, content, turnId };
  const prior = history.findIndex(e => e.role === 'assistant' && e.turnId === turnId);
  if (prior >= 0) return history.map((e, i) => i === prior ? reply : e);
  const asked = history.findIndex(e => e.role === 'user' && e.turnId === turnId);
  if (asked >= 0) return [...history.slice(0, asked + 1), reply, ...history.slice(asked + 1)].slice(-12);
  return appendIfUnplaced ? [...history, reply].slice(-12) : history;
}

const backgroundSockets = new Map<string, WebSocket>();

export type LandingAgentMode = 'fast' | 'deep';
export type LandingAgentStatus =
  | 'idle' | 'running' | 'clarifying' | 'defining' | 'done' | 'error' | 'cancelled';

export interface LandingAgentMessage {
  id: string;
  role: 'user' | 'agent';
  text: string;
  at: number;
}

export interface LandingAgentEvent {
  type: string;
  [k: string]: any;
}

export interface WorkerResultSnapshot {
  sub_task_id: string;
  description?: string;
  required_metadata?: any;
  spec?: any;
  sql?: string;
  columns?: string[];
  rows?: any[];
  row_count?: number;
  execution_time_ms?: number;
  status?: string;
  /** Unreviewed drafts that backed THIS sub-task's number (#151). */
  used_draft_definitions?: UsedDraftDefinition[];
  /** The answer's code-built label line (#1174 §4.11.4), and the record count the query computed. */
  label?: string;
  records?: number;
  /** #1174 §4.13: the gate-off post-answer checks' code-built lines. */
  caveats?: string[];
  /** #1499: an entity part's counts (its form, the rows returned, whether it was cut, the total) — only when sent. */
  entity?: EntityDisclosureSnapshot;
}

export interface LandingAgentResult {
  selectedPerspectives?: Array<{ id: string; name: string; description?: string }>;
  synthesis?: any;
  diagnose?: any;
  /** The ladder answer envelope (#180): WHAT (claims) · WHY · WHAT NEXT. Stored, not yet rendered. */
  ladder?: any;
  bundles?: any[];
  formatterResults?: Array<any>;
  formatterSummary?: string;
  formatterHighlights?: string[];
  formatterTitle?: string;
  workerResults?: WorkerResultSnapshot[];
  mission_status?: 'partial' | 'done' | 'declined';
}

/** A declined answer (#1174) keeps no rows: whatever it held of results is dropped, never stored or snapshotted. */
export function withoutRows(result: LandingAgentResult): LandingAgentResult {
  const rest: LandingAgentResult = { ...result };
  for (const key of ['workerResults', 'formatterResults', 'bundles', 'formatterSummary', 'formatterHighlights',
    'formatterTitle', 'synthesis'] as const) delete rest[key];
  return rest;
}

export interface UsedDraftDefinition {
  name?: string;
  display_name?: string;
  status?: string;
  author?: string;
}

export interface ClarificationQuestion {
  id: string;
  prompt: string;
  options?: string[];
  /** The router's clarifier (#760): the route each option re-asks the question under, in the options' order. */
  routes?: string[];
  /** A metric named outside the pick (#767): the perspective each option re-asks in, and for what. */
  scopes?: { perspective_id: string; for: 'question' | 'thread' }[];
  /** An ask about an earlier answer of the thread (#1807): the `earlier` each option re-asks with, in their order. */
  earliers?: EarlierChoice[];
  /** The reading question (#1818, `id: "reading"`): its session and its F3 block, rendered for this reader — each
   *  option is a `default_change` on its item, never a typed answer. */
  session?: string;
  defaults?: DefaultsBlock | null;
}

/** Which earlier answer of the thread a question means (#1807): "none" (Not that one), or an option the server issued
 *  on its own ask — that ask's session and the option's id (#1847 r4). What the option means is read from the
 *  server's record of the ask, never from the client. */
export type EarlierChoice = 'none' | { ask: string; option: number };

/** A one-click change of a stated default (#1808): the answer's session, the item's id and a server-issued option. */
export interface DefaultChange { session: string; default: string; option: number }

export interface Clarification {
  sub_task_id: string;
  description?: string;
  questions: ClarificationQuestion[];
}

/** One line of the answer gate's reading card (#1174 P3b): the slot, the input it states, the words the server's code
 *  rendered from what ran, and the SQL the compiler emitted for it. Plain text, never HTML. */
export interface ReadingItem { slot: string; source: string; text: string; compiled?: string }

/** One option on a reading card, by id (#1174 P3b): 0 is always "No — don't answer"; a (B) card's 1 confirms, an
 *  (A) card's 1…4 each name a metric by its catalog label and description. */
export interface ReadingOption { option_id: number; label: string; description?: string; action: string }

/** The answer gate's card (#1174 P3b): how the server read the question — (B) `confirm_reading`, the reading that ran,
 *  or (A) `several_metrics`, a phrase the catalog knows several ways, or (P4) `no_exact_field`, an entity's key the
 *  catalog lacks offered as the key fields it has, or (P5) `unstated_aggregation` / `average_unit`, how a measure is
 *  aggregated — and the options a click chooses by id. Sent
 *  live to the asker only, and on the owner's reopen; never on a thread snapshot. */
export interface ReadingConfirmation {
  session_id: string;
  ask_id: string;
  kind: 'confirm_reading' | 'several_metrics' | 'no_exact_field' | 'confirm_meaning' | 'unstated_aggregation'
    | 'average_unit' | 'rephrase' | 'retry' | 'restate' | 'window' | 'split' | 'without' | 'nearest_period'
    | 'year_or_value' | 'last_month_name';
  reading: ReadingItem[];
  options: ReadingOption[];
  phrase?: string;
  /** The option this browser sent (one click per card): the card is spent from then on. */
  chosen?: number;
}

/** A metric the asker just acknowledged and may confirm for everyone (#1174 §7): what opens C3's screen for it. */
export interface ConfirmForEveryoneLink { metric: string; label: string; draft_id: string; ref: string }

/** The links on an answer's terminal frame, each of its declared shape; anything else is dropped. */
export function confirmLinksOf(value: unknown): ConfirmForEveryoneLink[] {
  if (!Array.isArray(value)) return [];
  return value.filter((l: any) => l && ['metric', 'label', 'draft_id', 'ref'].every((k) => isStr(l[k]) && l[k]))
    .map((l: any) => ({ metric: l.metric, label: l.label, draft_id: l.draft_id, ref: l.ref }));
}

/** A choice on a reading card (#1174 P3b-2): the card's session and an option id — never a label, a spec or a hash. */
export interface ReadingChoice { session: string; option_id: number }

const isStr = (v: unknown): v is string => typeof v === 'string';
const isOptionId = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;

/** The cards whose options each name a reading (no reading lines): (A), P4's field and P5's aggregation cards, and
 *  §4.9's ask in words and Retry (G9: their one line is the server's fixed sentence, shown as the card's question),
 *  and G10's `restate` (L1's fallback: what ran, with what the question states in its place), and G4's `window` (a
 *  period the question names none of: its one line is the server's fixed sentence, each option a window by code), and
 *  G12's L3 offers — each aligned result on its own (`split`), the nearest reading `without` an unstatable part, the
 *  `nearest_period`s with a complete day, and `year_or_value` — each headed by the server's fixed sentence. */
const OFFERS: ReadonlyArray<string> = ['several_metrics', 'no_exact_field', 'unstated_aggregation', 'average_unit',
  'rephrase', 'retry', 'restate', 'window', 'split', 'without', 'nearest_period', 'year_or_value', 'last_month_name'];

/** The card as the turn keeps it: only the fields the card shows, each of its declared type; a frame of any other
 *  shape is no card (null), so nothing malformed is ever offered for a click. */
export function readingCardOf(evt: LandingAgentEvent): ReadingConfirmation | null {
  if (!isStr(evt.session_id) || !evt.session_id || !isStr(evt.ask_id) || !evt.ask_id) return null;
  if (evt.kind !== 'confirm_reading' && evt.kind !== 'confirm_meaning' && !OFFERS.includes(evt.kind)) return null;
  if (!Array.isArray(evt.reading) || !Array.isArray(evt.options)) return null;
  const reading = evt.reading.map((i: any) => (i && isStr(i.slot) && isStr(i.source) && isStr(i.text)
    ? { slot: i.slot, source: i.source, text: i.text, ...(isStr(i.compiled) && i.compiled ? { compiled: i.compiled } : {}) } : null));
  const options = evt.options.map((o: any) => (o && isOptionId(o.option_id) && isStr(o.label) && isStr(o.action)
    ? { option_id: o.option_id, label: o.label, action: o.action,
      ...(isStr(o.description) && o.description ? { description: o.description } : {}) } : null));
  if (reading.some((i: ReadingItem | null) => !i) || options.some((o: ReadingOption | null) => !o)) return null;
  const ids = options.map((o: ReadingOption | null) => o!.option_id);
  if (new Set(ids).size !== ids.length || !ids.includes(0) || ids.length < 2) return null;
  if (!OFFERS.includes(evt.kind) && !reading.length) return null;
  return { session_id: evt.session_id, ask_id: evt.ask_id, kind: evt.kind, reading: reading as ReadingItem[],
    options: options as ReadingOption[], ...(isStr(evt.phrase) ? { phrase: evt.phrase } : {}) };
}

/** What a click on a reading card says as the asker's turn (#1174 P3b): a fixed, content-free string by option id —
 *  never the option's label or description, nor any line of the reading, which live only in the server's ask store
 *  and the live card. */
export function choiceText(kind: ReadingConfirmation['kind'], optionId: number): string {
  if (optionId === 0) return 'No — don\'t answer';
  return OFFERS.includes(kind) ? `Option ${optionId}` : 'Yes';
}

/** A turn as a thread snapshot keeps it (#1174 P3b §5): without its reading card — the card is the asker's live one,
 *  rebuilt by the server for its owner on a reopen while it is on offer. */
export function withoutReadingCard<T extends { readingConfirmation?: ReadingConfirmation | null }>(turn: T): T {
  if (!turn || !('readingConfirmation' in turn)) return turn;
  const { readingConfirmation, ...rest } = turn;
  return rest as T;
}

export interface HistoryTurn {
  role: 'user' | 'assistant';
  content: string;
  /** The turn this entry belongs to, when the entry was written by a why-walk (#184): a late or re-run reply finds its own question by id, never by text. Stripped before the transcript is sent. */
  turnId?: string;
}

export interface TurnFeedback {
  rating: 'up' | 'down';
  tags?: string[];
  parts?: string[];
  comment?: string;
  submittedAt: number;
}

export interface LandingAgentTurn {
  id: string;
  userText: string;
  /** What this turn took from the thread's context rather than from the question ([{field, source}]). */
  inherited?: Array<{ field: string; source: string }>;
  /** A walk opened from a number: which rung the asker came for — WHY (the default) or WHAT NEXT. */
  intent?: 'why' | 'next';
  /** This turn is a walk (a why-engine answer), also while it re-runs with its result cleared — the tab's rail stays with it. */
  isWalk?: boolean;
  foreground?: boolean;
  /** Asked here, so what finishes on this screen is the answer to save (`storedTurn`). */
  shownLive?: boolean;
  /** The finished answer stored as it was shown: the server keeps and returns it untouched. */
  savedAsShown?: boolean;
  diagnosisParentTurnId?: string;
  analysisOperationId?: string;
  analysisSequence?: number;
  analysisDisconnected?: boolean;
  analysisCancelPending?: boolean;
  analysisAwaitingLadder?: boolean;
  diagnosisOperationId?: string;
  diagnosisPreviousOperationId?: string;
  diagnosisPreviousOperationIds?: Array<string | null>;
  diagnosisProgress?: DiagnosisRun;
  diagnosisDisconnected?: boolean;
  diagnosisCancelPending?: boolean;
  sentAt: number;
  status: LandingAgentStatus;
  result: LandingAgentResult | null;
  events: LandingAgentEvent[];
  clarification: Clarification | null;
  /** The agent hit a term the semantic layer has never heard of (#151). */
  definitionRequest: DefinitionRequest | null;
  error: string | null;
  sessionId?: string | null;
  /** The thread the answer's session belongs to, when it was restored from a link (?session=) rather than asked here. */
  sessionThreadId?: string | null;
  feedback?: TurnFeedback | null;
  /** The person pressed Stop (or closed the tab) on this turn: a late answer is ignored, unlike a turn merely superseded by a newer question, which still gets its answer (#184). */
  stopped?: boolean;
  /** The perspective this turn was asked in — a correction binds to it (#186), not to whatever is selected later. */
  perspectiveId?: string | null;
  /** The thread's pick when this turn was sent (#767): the next question names a change from it as `scope_from`. */
  threadPick?: string | null;
  /** The ask exactly as it went on the wire (#760): Change and a router-clarifier option re-send it, changing only
   *  the route — never rebuilt from the thread's current state. Persisted with the thread's snapshot; a replayed
   *  answer gets it from the server (`data.ask`). */
  askedRequest?: AskRequest | null;
  /** Prior work (#762, #911): the offer itself (`prior_work_offered`), a Refresh's — or a repeat's — then vs now
   *  (`prior_work_refreshed`), or the earlier analyses an answer that ran fresh names (`prior_work_related`), as
   *  the server read it for this asker. The server keeps it by reference and reads it again for whoever opens the thread. */
  priorWork?: PriorWorkOffered | PriorWorkRefreshed | PriorWorkRelated | null;
  /** The document an attached file was read into (#763): its numbers, each with where it stands against the catalog. */
  document?: DocumentAnswer | null;
  /** The answer gate's reading card (#1174 P3b): how the question was read, and the options a click chooses by id. */
  readingConfirmation?: ReadingConfirmation | null;
  /** The asker acted on this turn's card without asking again (#1503, flag on only): set by `resolveWait`, kept on the snapshot. */
  waitResolved?: boolean;
  /** After a "Yes, that's what I mean" (#1174 §7), each metric the asker may confirm for everyone in Studio (C3). */
  confirmForEveryone?: ConfirmForEveryoneLink[] | null;
  /** The status a `mission_error` carried (409: the card's choice was already made). */
  errorStatus?: number | null;
  /** #1808: a change of one of this answer's defaults the server could no longer make — the answer stays as it was. */
  defaultsNotice?: string | null;
  /** Listed without its answer (#1270): the server rebuilds it when the thread is opened, and the open fills it in. */
  answerPending?: boolean;
  /** The titles-only list's turn (#1596): its question only, its answer loaded when the thread is opened. */
  listed?: boolean;
  /** The server's read of a listed turn's answer (#1596): it waits on the asker (`needsYou`'s rule) / it walked. */
  waiting?: boolean;
  walked?: boolean;
  /** C8 (#1672): the headline and highlights as they are written (`answer_delta`), until each one's final frame. Live
   *  only: never in `events`, never on the thread snapshot; a new socket starts without it. */
  streaming?: AnswerStreaming | null;
  /** C8 (#1672): what had streamed when the asker pressed Stop, shown under "Stopped — this answer was not finished."
   *  Kept in the asker's own snapshot; a final frame clears it (a Stop refused because the answer was stored). */
  stoppedWords?: StoppedWords | null;
  /** F4 (#1858): the live `answer_progress` frames (the route's words and usual time, the planner's early reading).
   *  Live only: never in `events`, never on the thread snapshot — `mission_planned` supersedes it on every path. */
  progress?: LiveProgress | null;
  /** F4 (#1858 §5): the asker left this running turn (its thread was parked) — in memory only, never on the snapshot.
   *  A turn restored on a page that never watched it reads as away too (`wasAway`). */
  wasAway?: boolean;
}

/** One number an attached file printed (#763), as the Document agent mapped it — never silently: `matches` names one
 *  metric that reproduces it, `ambiguous` two or more, `not_in_catalog` none; `null` was not checked. */
export interface DocumentNumber {
  key: string; quote: string; label: string; rendered: string; page: number | null; page_word: string;
  value: number | null; unit: string | null; tier: string;
  match: 'matches' | 'mismatch' | 'ambiguous' | 'not_in_catalog' | null;
  candidates: Array<{ candidate: string; metric: string; metric_qid: string; perspective: string | null; perspective_name: string | null;
    dimension: string | null; filters: Array<{ dimension: string; operator: string; value: unknown }>;
    value: number | null; rendered: string | null; match: string }>;
  reason: string | null;
}

/** The `document_result` event: the file, read, and the question back — which number to talk about. */
export interface DocumentAnswer {
  type: 'document_result'; session_id: string; upload_id: string; name: string; prompt: string;
  status: 'ok' | 'error'; error: string | null;
  document: { name: string; kind: string; pages: number; window: { start: string; end: string } | null; document_id: string; private: boolean } | null;
  claims: DocumentNumber[]; warnings: string[]; below_bar?: number;
}

export interface PriorWorkOffered { type: 'prior_work_offered'; session_id?: string; matches: PriorWorkMatch[] }

export interface PriorWorkWindow { start?: string | null; end?: string | null; preset?: string | null }
/** A walk's verdict as the server disclosed it to this asker: its headline and its policy block. */
export interface PriorWorkVerdict { headline: string | null; policy?: { hidden_count?: number; raise_to?: string | null } | null }
/** One earlier analysis offered before Why or Data (#762): Open the original. */
export interface PriorWorkMatch {
  ref: { source: 'own' | 'shared' | 'report'; id: string; turn_id: string | null };
  kind?: 'thread' | 'doc' | 'report' | string;
  title?: string;
  question?: string | null;
  when?: string | null;
  href?: string;
  mine?: boolean;
  route?: string | null;
  metrics?: string[];
  window?: PriorWorkWindow | null;
  verdict?: PriorWorkVerdict | null;
  /** What a Refresh would run, read from the earlier answer's server record; absent: no Refresh. */
  refresh?: { window: PriorWorkWindow; perspective: string } | null;
  /** On a list asked for explicitly (#911): the org-mate who asked it, and what it found as disclosed to this reader. */
  by?: string | null;
  found?: string | null;
}
/** One number of a Refresh's then vs now, its row typed (a null key value is SQL NULL). */
export interface PriorWorkRow { key: string; value: string | number | boolean | null }
export interface PriorWorkChange { metric: string; row: PriorWorkRow[]; then: number; now: number; delta: number; pct: number | null }
/** A Refresh (#762): what it re-ran and what changed since, read at every read from the earlier answer's server record. */
export interface PriorWorkRefreshed {
  type: 'prior_work_refreshed'; session_id?: string | null; route?: string | null;
  source: { ref: PriorWorkMatch['ref']; title?: string; question?: string | null; when?: string | null; href?: string; mine?: boolean;
    by?: string | null; day?: string | null } | null;
  /** The same question asked again, answered from the earlier analysis (#911 part C): what it is based on (while the
   *  asker may open it) and the window it now covers, each a sentence the server wrote. */
  basis?: 'repeat' | null; based_on?: string | null; covers?: string | null;
  available?: boolean; comparable?: boolean; summary?: string;
  then_window?: PriorWorkWindow | null; now_window?: PriorWorkWindow | null;
  changes?: PriorWorkChange[]; lines?: string[]; gone?: unknown[]; gone_lines?: string[]; not_comparable?: number;
}
/** One earlier analysis named on an answer that ran fresh (#911 part C): how it matched, the sentence that names it
 *  (`text`) and, for the same question not answered from, why (`why`). */
export interface PriorWorkLine extends PriorWorkMatch {
  match: 'same' | 'related'; reason?: string | null; day?: string | null; text: string; why?: string | null;
}
/** The earlier analyses an answer that ran fresh names, read for this asker now: the card shows the first in one line. */
export interface PriorWorkRelated { type: 'prior_work_related'; session_id?: string | null; lines: PriorWorkLine[] }
/** The asker's choice on an offer, sent beside the request like a Change: Start fresh skips the step; a Refresh re-runs
 *  that earlier answer on a new window. */
export type PriorChoice = 'fresh' | { refresh: { source: 'own' | 'shared'; id: string; turn_id: string } };

/**
 * A follow-up about a displayed answer (#767) is asked about THAT answer: in the perspective it was asked in and
 * with the scope it wrote (its ladder's thread_context) — for that question only, never the thread's pick. Every
 * control on an answer (Trace why, the follow-up chips, What's next, a clarification's answer, definition capture's
 * re-asks) sends these opts.
 */
export function followUpOf(turn: Pick<LandingAgentTurn, 'askedRequest' | 'perspectiveId' | 'result'> | null | undefined,
  { sameQuestion = false }: { sameQuestion?: boolean } = {}): { perspectiveId?: string; threadContext: ThreadContext | null } {
  const perspective = turn?.askedRequest?.perspective_id || turn?.perspectiveId || undefined;
  // the same question asked again (definition capture) keeps the scope it was asked with; a follow-up takes the answer's
  const threadContext = sameQuestion && turn?.askedRequest ? turn.askedRequest.thread_context ?? null
    : (turn?.result?.ladder?.thread_context as ThreadContext | undefined) ?? null;
  return { ...(perspective ? { perspectiveId: perspective } : {}), threadContext };
}

/** Every field `sendLandingAgentQuery` puts on the wire for a question — all but the transport (operation id,
 *  resume, after) and the Change (route, route_from). */
export interface AskRequest {
  query: string;
  perspective_id: string;
  domain_id: string | null;
  mode: string;
  thread_id: string;
  history: { role: string; content: string }[];
  thread_context: ThreadContext | null;
  use_definitions: string[];
  /** The objective the question is asked inside (#766); a re-ask keeps its own (absent on a turn asked before #766). */
  objective_id?: string | null;
  /** A file attached in the composer (#763): the Document agent reads it. */
  attachment: Attachment | null;
}

/**
 * One thread's conversation, parked while another tab is active (#182).
 * The socket rides along: a run keeps going in a background tab, its events
 * land here, and `unread` lights the tab's dot when it finishes.
 */
export interface ParkedConversation {
  turns: LandingAgentTurn[];
  history: HistoryTurn[];
  messages: LandingAgentMessage[];
  events: LandingAgentEvent[];
  status: LandingAgentStatus;
  result: LandingAgentResult | null;
  clarification: Clarification | null;
  definitionRequest: DefinitionRequest | null;
  error: string | null;
  ws: WebSocket | null;
  unread: boolean;
  /** The last answer's scope, carried with the parked thread. */
  threadContext?: ThreadContext | null;
}

export type VerdictPanelState =
  | { kind: 'envelope'; envelope: any; sessionId?: string | null; title?: string | null; turnId?: string | null;
      /** the perspective the walk was asked in — a confirmation, a correction, a contribution bind to it */
      perspective?: string | null }
  | { kind: 'section'; title: string; why: Record<string, any>; reviewTitle?: string | null;
      /** the number itself, as it stands on the review — what a confirmation or a correction is bound to */
      claim?: Record<string, any> | null; perspective?: string | null;
      /** a pinned number opened from its tile (#317): the panel shows where it came from, its analyses, its commentary — read live from the review */
      section?: { threadId: string; sectionId: string } | null };

export interface LandingAgentSlice {
  landingAgentDomains: DataDomain[];
  /** Conversations of threads that are open but not on screen, by thread id. */
  parkedConversations: Record<string, ParkedConversation>;
  selectedLandingAgentDomainId: string | null;  // null = All domains
  perspectivesList: Perspective[];
  selectedPerspectiveId: string | null;
  landingAgentMode: LandingAgentMode;
  landingAgentMessages: LandingAgentMessage[];
  landingAgentEvents: LandingAgentEvent[];
  landingAgentStatus: LandingAgentStatus;
  landingAgentResult: LandingAgentResult | null;
  landingAgentClarification: Clarification | null;
  landingAgentDefinitionRequest: DefinitionRequest | null;
  landingAgentError: string | null;
  landingAgentWs: WebSocket | null;
  landingAgentHistory: HistoryTurn[];
  landingAgentTurns: LandingAgentTurn[];
  /**
   * What the last answer on screen was scoped to (server-written): sent
   * back with the next question so a follow-up inherits the metric, the
   * groupings, the filters and the window it omits. Null until an answer lands.
   */
  landingAgentThreadContext: ThreadContext | null;

  fetchLandingAgentPerspectives: () => Promise<void>;
  setSelectedLandingAgentDomain: (id: string | null) => void;
  setSelectedPerspective: (id: string) => void;
  setLandingAgentMode: (m: LandingAgentMode) => void;
  /**
   * `useDefinitions` is a per-question opt-in to answer with somebody's
   * unreviewed draft. Passed per call and never stored: a remembered choice
   * would be silent resolution wearing a hat, which is the thing the review
   * gate exists to prevent.
   */
  /** Returns whether the question actually started — false when it was refused
   *  (no perspective yet), so a composer can keep what was typed. */
  sendLandingAgentQuery: (text: string, opts?: { resumeTurnId?: string; threadContext?: ThreadContext | null; useDefinitions?: string[]; source?: { kind: string; id: string } | null; route?: string; routeFrom?: string | null; reask?: AskRequest | null; perspectiveId?: string; scopeFrom?: string | null; scopeFor?: 'question' | 'thread'; attachment?: Attachment | null; prior?: PriorChoice; answers?: string | null; choice?: ReadingChoice | null; earlier?: EarlierChoice; replaces?: string | null; defaultChange?: DefaultChange | null }) => boolean;
  /** A click on a reading card (#1174 P3b): sends `choice {session, option_id}` beside a new question, once per card —
   *  false when the card is already spent, the option is not on it, or the question did not start. */
  chooseReading: (turnId: string, optionId: number) => boolean;
  /** A click on one of an answer's stated defaults (#1808): re-sends the turn's own request with `default_change` and
   *  `replaces`, so the turn is replaced in place once the server says it recorded the change. */
  changeDefault: (turnId: string, id: string, option: number) => boolean;
  /**
   * The one place a card action that ends a wait on the asker without a newer question records it (#1503): the
   * turn is marked `waitResolved` and the thread persisted, so a reopen or a reload keeps it. Only while the
   * simplified app is on (its sidebar's needs-you marker is the one reader); with it off nothing is written.
   */
  resolveWait: (turnId: string) => void;
  /** The asker's own pick (#767): the thread's perspective, and its domain with it when the domain layer is on. */
  pickPerspective: (id: string) => void;
  /** Re-ask a turn's own request in another perspective (#767): for this question only, or as the thread's new pick.
   *  The only ways an answer changes the thread's pick are the asker's: this with 'thread', and the picker. */
  reaskInPerspective: (turn: LandingAgentTurn, perspectiveId: string, scopeFor: 'question' | 'thread', opts?: { replaces?: string | null; route?: string | null }) => boolean;
  cancelLandingAgent: () => void;
  resetLandingAgent: () => void;
  /**
   * Park the active conversation under its thread id and clear the screen —
   * WITHOUT closing its socket, so a run in flight carries on in the
   * background. Used when switching tabs or going Home.
   */
  parkConversation: () => void;
  /** Bring a parked conversation back on screen. False if nothing is parked. */
  unparkConversation: (threadId: string) => boolean;
  /** Forget a parked conversation (its thread was closed or deleted), closing its socket. */
  dropParkedConversation: (threadId: string) => void;
  /**
   * Forget EVERY parked conversation without storing anything: the
   * workspace changed, and a transcript from the previous account or org
   * must not be written under the new one.
   */
  discardParkedConversations: () => void;
  submitTurnFeedback: (turnId: string, feedback: TurnFeedback) => Promise<void>;
  clearTurnFeedback: (turnId: string) => void;
  resumeDiagnosis: (turnId: string) => void;
  stopDiagnosis: (turnId: string) => void;
  stopBackgroundAnalysis: (turnId: string) => Promise<void>;
  recoverLandingAgentAnswer: (turnId: string, sessionId: string, signal: AbortSignal) => Promise<'pending' | 'done' | 'failed' | 'not_found'>;
  restoreLandingAgentSession: (sessionId: string, signal?: AbortSignal) => Promise<boolean>;
  /**
   * WHY from a number (#184): the popover's claim becomes a turn in this
   * thread — question, running, then the WHY card — without a round trip
   * through words. `subject` is how the number reads ("West orders for region West").
   */
  /** `source` (#189): what the walk was opened FROM — a review section — stamped on the thread it mints. `intent: 'next'` lands on WHAT NEXT. */
  askWhyForClaim: (claim: Record<string, any>, subject: string, source?: { kind: string; id: string } | null, opts?: { intent?: 'why' | 'next' }) => void;
  /** The verdict side panel (the mock's "Why this verdict"): a walk's envelope, or a review section's stored walk. */
  verdictPanel: VerdictPanelState | null;
  openVerdictPanel: (panel: VerdictPanelState) => void;
  closeVerdictPanel: () => void;
  /** #1489: whether a turn's supporting numbers (its full table and chart, behind its answer) are open, by turn id.
   *  Kept here, not in the card, so switching tabs or remounting the card keeps it; never persisted. */
  answerSupportingOpen: Record<string, boolean>;
  setAnswerSupportingOpen: (turnId: string, open: boolean) => void;
  /** "Keep digging": append a deeper analysis without replacing the completed answer. */
  digDeeper: (turnId: string) => void;
  /** #1506: Retry for a walk opened from a number, which records no ask: the same walk again (its metric, window,
   *  filters, effort and perspective as its envelope states them), as a new turn. False when there is nothing to re-run. */
  retryWalk: (turnId: string) => boolean;
  /** A correction was saved (#186): lay it onto the turn's envelope — original kept as the first layer — and persist. */
  correctWhyTurn: (turnId: string, saved: Record<string, any>) => void;
  /** A contribution was saved, or its dry run landed (#193): lay it onto the turn's envelope as a driver and persist. */
  contributeWhyTurn: (turnId: string, saved: Record<string, any>) => void;
  /** Raise the evidence bar for one why-turn (#187): re-run its walk at `bar`, in place; not remembered. */
  raiseBar: (turnId: string, bar: string) => void;
}

function wsUrl(): string {
  const envUrl = process.env.REACT_APP_AGENT_WS_URL;
  if (envUrl) return withWsAuth(envUrl);
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.hostname;
  // CRA dev server runs on 3000; backend on 5001. In prod assume same origin.
  const port = window.location.port === '3000' ? '5001' : window.location.port;
  return withWsAuth(`${proto}//${host}${port ? ':' + port : ''}/api/agent/ws`);
}

/**
 * Which question the socket layer is working on. A reconnect waits on a token
 * refresh with no socket in the store, so during that window there is nothing
 * for `closeAgentWs` to mark: a question asked (or a reset done) while the
 * refresh is in flight would be trampled by the previous question reconnecting
 * and reporting into whatever turn is newest. Every attempt captures the
 * generation it belongs to and gives up once it moves.
 */
let _attemptGen = 0;

/**
 * F4 (#1858 §5, r1-B4): the turns this page is watching — asked here, never left. A turn this page did not ask (it was
 * restored from a thread snapshot after a reload, or opened from the list) is not in it, and stays out when its socket
 * is re-attached; parking a running turn takes it out. A transport reconnect touches neither, so a turn watched from
 * start to end never reads as "away".
 */
export const liveThisPage = new Set<string>();

/** Whether the asker left this running turn and came back (§5): its thread was parked, or this page never watched it. */
export function wasAway(turn: Pick<LandingAgentTurn, 'id' | 'status' | 'wasAway'>): boolean {
  return turn.status === 'running' && (turn.wasAway === true || !liveThisPage.has(turn.id));
}

/**
 * Per-thread attempt generations (#182). Each thread's run is its own: a new
 * question in thread A must not invalidate the run still going in thread B.
 */
const _threadGens = new Map<string, number>();
function bumpThreadGen(threadId: string | null | undefined): number {
  const next = (_threadGens.get(threadId || '') || 0) + 1;
  _threadGens.set(threadId || '', next);
  return next;
}
function threadGen(threadId: string | null | undefined): number {
  return _threadGens.get(threadId || '') || 0;
}

/**
 * Close a socket we are finished with, marking it so its own `onclose` knows
 * the close was ours. Without the mark, a socket abandoned before it delivered
 * anything is indistinguishable from a rejected handshake and would trigger the
 * stale-token reconnect — reviving a question the user has already moved on
 * from, on top of the socket that replaced it.
 */
/** #1818: the session of the turn whose answer holds this very claim object (a tile, cell or chart point of it). */
export function claimSession(turns: LandingAgentTurn[], claim: Record<string, any>): string | null {
  const holds = (node: any, depth: number): boolean => {
    if (node === claim) return true;
    if (!node || typeof node !== 'object' || depth > 6) return false;
    return Object.values(node).some((v) => holds(v, depth + 1));
  };
  const turn = turns.find((t) => holds((t as any).result?.ladder?.what, 0));
  return (turn as any)?.sessionId || null;
}

function detachBackgroundSockets(turns: LandingAgentTurn[]): void {
  for (const turn of turns) {
    if (!turn.analysisOperationId) continue;
    const socket = backgroundSockets.get(turn.analysisOperationId);
    if (socket) {
      (socket as any).__voyagerAbandoned = true;
      try { socket.close(); } catch {}
      backgroundSockets.delete(turn.analysisOperationId);
    }
  }
}

function closeAgentWs(ws: WebSocket | null, threadId?: string | null): void {
  _attemptGen += 1;
  bumpThreadGen(threadId);
  if (!ws) return;
  (ws as any).__voyagerAbandoned = true;
  try { ws.close(); } catch {}
}

/**
 * A new question replaces the run in its thread: the socket is abandoned and
 * its close handler steps aside, so the turn it was answering would stay
 * "running" forever — on screen and in the snapshot. It is cancelled here,
 * the same way Stop cancels it.
 */
function settleSuperseded(turns: LandingAgentTurn[]): LandingAgentTurn[] {
  return turns.map((t) => ({ ...t, foreground: false,
    ...(t.status === 'running' && !t.diagnosisOperationId && !t.analysisOperationId ? { status: 'cancelled' as const } : {}) }));
}

/**
 * A why-walk as a turn (#184). It is an HTTP call, not a socket, but it is a
 * question in this thread like any other: it replaces the run in this thread,
 * lands on screen or in the parked conversation, reaches the durable
 * snapshot at every step, and is dropped if a newer question superseded it.
 * With `replaceTurnId` the same turn resumes or reruns with a different evidence bar.
 */
/**
 * A review document is not a conversation: a question asked while one is on
 * screen — Why or What's next from a pinned number's popover — must open as
 * its own thread, never run inside the review (where it would have no screen
 * to land on and would write a transcript onto the document). The review is
 * left the way its own Why chip leaves it: parked, its tab kept.
 */
function leaveReviewOnScreen(get: () => VoyagerState, set: (patch: Partial<VoyagerState>) => void, source?: { kind: string; id: string } | null): void {
  const st = get() as any;
  const current = (st.savedThreads as any[] | undefined)?.find((t) => t.id === st.currentThreadId);
  if (source) set({ pendingThreadSource: source } as any);
  if (current?.kind !== 'doc') return;
  if (typeof st.clearLandingChat === 'function') st.clearLandingChat();
  else set({ currentThreadId: null } as any);
}

/** The engine's half-open end (`2026-09-01T00:00:00`) as the inclusive date a claim carries (`2026-08-31`). */
export function inclusiveEnd(value: string): string {
  const text = String(value);
  if (text.length === 10) return text;
  const d = new Date(text.endsWith('Z') || /[+-]\d\d:\d\d$/.test(text) ? text : `${text}Z`);
  if (Number.isNaN(d.getTime())) return text.slice(0, 10);
  if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0) d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/** The context a switched perspective keeps: the window, and the thread's last route (#760: the next question's
 *  previous route, which no catalog names); nothing named in the old perspective's catalog. */
export function contextForPerspective(ctx: ThreadContext | null, perspective: string | null): ThreadContext | null {
  if (!ctx) return null;
  if (ctx.perspective === perspective) return ctx;
  return { perspective, metrics: [], primary_metric: null, dimensions: [], filters: [], window: ctx.window ?? null, definitions: [], session_id: null,
    ...(ctx.route ? { route: ctx.route } : {}) };
}

/** The context a walk opened from a number starts with: that number's own grounding (#189). */
export function contextFromClaim(claim: Record<string, any> | null | undefined): ThreadContext | null {
  const g = claim?.grounding;
  if (!g?.metric_qid) return null;
  return {
    perspective: g.perspective || null,
    metrics: [g.metric_qid], primary_metric: g.metric_qid,
    dimensions: Object.keys(g.row || {}).filter((k) => !(g.time_columns || {})[k]),
    filters: [...(Array.isArray(g.filters) ? g.filters.filter((f: any) => f && typeof f === 'object') : []),
      ...Object.entries(g.row || {}).filter(([k, v]) => v != null && !(g.time_columns || {})[k]).map(([k, v]) => ({ field: k, op: '=', value: v }))],
    window: g.window?.start && g.window?.end ? { start: g.window.start, end: g.window.end } : null,
    definitions: [], session_id: null,
  };
}

/** The conversation an answer's frames land in, besides its turn. */
export type TurnConversation = Pick<ParkedConversation, 'events' | 'status' | 'result' | 'history'>;

/**
 * One frame of an answer applied to its turn (#760): the live socket's handler and a restored answer (the server's
 * replayed frames, `restoreLandingAgentSession`) both paint through this one reducer, so a reopened answer is the
 * answer as it was live. `acceptsContext` says whether a ladder's scope becomes the thread's. Null for a frame that
 * changes nothing (a ladder older than the one held).
 */
export function reduceTurnEvent(conv: TurnConversation, turn: LandingAgentTurn, evt: LandingAgentEvent,
  acceptsContext: (ctx: ThreadContext) => boolean): { patch: Partial<LandingAgentSlice>; turnPatch: Partial<LandingAgentTurn> } | null {
  // a streamed word (C8, #1672) is never a frame of the answer: the live socket applies it (`reduceAnswerDelta`)
  if (evt.type === 'answer_delta') return null;
  // F4 (#1858): the live progress frames land on the turn's `progress`, never in its events (they are never stored)
  if (evt.type === 'answer_progress') {
    const progress = liveProgressAfter(turn.progress, evt);
    return progress ? { patch: {}, turnPatch: { progress } } : null;
  }
  // a reading card (#1174 P3b) is kept in the turn's events by its ids only, as a durable run keeps it: its reading and
  // options live on `readingConfirmation`, which the thread snapshot never carries
  const kept = evt.type === 'reading_confirmation_required'
    ? { type: evt.type, session_id: evt.session_id, ask_id: evt.ask_id } as LandingAgentEvent : evt;
  const events = [...conv.events, kept];
  const patch: Partial<LandingAgentSlice> = { landingAgentEvents: events };
  const turnPatch: Partial<LandingAgentTurn> = { events, analysisDisconnected: false,
    ...(evt.run_seq ? { analysisSequence: evt.run_seq } : {}) };
  if (evt.type === 'analysis_started' && evt.session_id) turnPatch.sessionId = evt.session_id;
  // C8 (#1672): a part's final frame replaces its streamed words, a terminal frame or the ladder clears them all
  const streaming = streamingAfterFrame(turn.streaming, evt);
  if (streaming !== undefined) turnPatch.streaming = streaming;
  // and the words a Stop kept give way to the answer's final frames (a Stop refused because the answer was stored)
  if (turn.stoppedWords && ['formatter_result', 'synthesis_result', 'mission_complete', 'mission_error', 'ladder'].includes(evt.type)) {
    turnPatch.stoppedWords = null;
  }

  if (evt.type === 'perspectives_selected') {
    const selected = evt.perspectives || [];
    patch.landingAgentResult = { ...(conv.result || {}), selectedPerspectives: selected };
    turnPatch.result = patch.landingAgentResult;
    if (selected.length === 1) turnPatch.perspectiveId = selected[0].id;
  } else if (evt.type === 'mission_planned') {
    if (evt.session_id) turnPatch.sessionId = evt.session_id;
    // what the plan took from the thread's context rather than from the question
    const inherited = (evt.sub_tasks || []).flatMap((st: any) => (Array.isArray(st?.inherited) ? st.inherited : []))
      .filter((i: any) => i && typeof i.field === 'string');
    turnPatch.inherited = inherited.filter((i: any, n: number, all: any[]) => all.findIndex((o) => o.field === i.field) === n);
  } else if (evt.type === 'worker_result') {
    const existing = conv.result || {};
    const prevList = existing.workerResults || [];
    const startedEvt = [...events].reverse().find(
      (e) => e.type === 'sub_task_started' && e.sub_task_id === evt.sub_task_id,
    );
    const snapshot: WorkerResultSnapshot = {
      sub_task_id: evt.sub_task_id,
      description: startedEvt?.description,
      required_metadata: startedEvt?.required_metadata,
      spec: evt.spec,
      sql: evt.sql,
      columns: evt.columns,
      // #1499: an entity list's whole returned list, live only (`rows_live`); every other part its preview
      rows: Array.isArray(evt.rows_live) ? evt.rows_live : evt.rows_preview,
      row_count: evt.row_count,
      execution_time_ms: evt.execution_time_ms,
      // F4 (#1858 §4, Q3): an F4 answer's numbers are held until code accepts the part (`part_accepted`) — never shown
      // before, whatever the review says
      status: evt.status === 'done' && plannedOf({ events }) ? PENDING_ACCEPTANCE : evt.status,
      // Held on the SNAPSHOT, not accumulated on the result. A retried
      // sub-task replaces its own snapshot, so a rejected attempt's
      // provenance cannot outlive it — and two parallel workers cannot
      // overwrite each other's.
      used_draft_definitions: evt.used_draft_definitions || [],
      // #1174 §4.11.4 (G13): the label line an accepted answer carries — only when the server sent one
      ...(typeof evt.label === 'string' ? { label: evt.label } : {}),
      ...(typeof evt.records === 'number' ? { records: evt.records } : {}),
      // #1174 §4.13: the gate-off post-answer checks' code-built lines — only when the server sent them
      ...(Array.isArray(evt.caveats)
        ? { caveats: evt.caveats.filter((c: unknown): c is string => typeof c === 'string') } : {}),
      // #1499: an entity part's counts — the count line is computed from the rows this snapshot carries
      ...(entitySnapshot(evt.entity) ? { entity: entitySnapshot(evt.entity) } : {}),
    };
    const idx = prevList.findIndex((w) => w.sub_task_id === evt.sub_task_id);
    const nextList = idx >= 0
      ? prevList.map((w, i) => (i === idx ? snapshot : w))
      : [...prevList, snapshot];
    patch.landingAgentResult = { ...existing, workerResults: nextList };
    turnPatch.result = patch.landingAgentResult;
  } else if (evt.type === PART && typeof evt.sub_task_id === 'string') {
    // F4 (#1858 §4a, §4b rule 6): code's decision on a part. Accepted: its rows may show. Not accepted: in this same
    // step, every retained copy of the part's numbers — both event arrays and the worker snapshot — becomes the public
    // form (no row, no SQL, no gatekeeper reason), then the decision itself is kept
    const sid = evt.sub_task_id as string;
    const existing = conv.result || {};
    const list = existing.workerResults || [];
    let next = list;
    if (evt.accepted === true) {
      next = list.map((w) => (w.sub_task_id === sid && w.status === PENDING_ACCEPTANCE ? { ...w, status: 'done' } : w));
    } else {
      const projected = [...project(conv.events, new Set([sid])), kept];
      patch.landingAgentEvents = projected;
      turnPatch.events = projected;
      next = list.map((w) => (w.sub_task_id === sid ? publicSnapshot(sid) : w));
    }
    if (next !== list) {
      patch.landingAgentResult = { ...existing, workerResults: next };
      turnPatch.result = patch.landingAgentResult;
    }
  } else if (evt.type === 'document_result') {
    // an attached file, read (#763): the card asks which of its numbers to talk about
    turnPatch.document = evt as unknown as DocumentAnswer;
    if (evt.session_id) turnPatch.sessionId = evt.session_id;
  } else if (evt.type === 'clarification_required') {
    patch.landingAgentStatus = 'clarifying';
    patch.landingAgentClarification = {
      sub_task_id: evt.sub_task_id,
      description: evt.description,
      questions: evt.questions || [],
    };
    turnPatch.status = 'clarifying';
    turnPatch.clarification = patch.landingAgentClarification;
  } else if (evt.type === 'reading_confirmation_required') {
    // the answer gate's card (#1174 P3b): the asker confirms the reading, or picks a metric, by option id
    const card = readingCardOf(evt);
    if (card) turnPatch.readingConfirmation = card;
  } else if (evt.type === 'definition_required') {
    // The question named a term the layer has never heard of (#151). A
    // distinct status from 'clarifying': the way forward is to define the
    // term, not to answer a multiple-choice question.
    patch.landingAgentStatus = 'defining';
    const request = {
      sub_task_id: evt.sub_task_id,
      description: evt.description,
      term: evt.term,
      kind_hint: evt.kind_hint,
      perspective: evt.perspective,
      perspective_name: evt.perspective_name,
      kinds: evt.kinds || [],
      field_groups: evt.field_groups || [],
      field_locations: evt.field_locations || [],
      nearby: evt.nearby || [],
      nearest: evt.nearest || [],
      pending_drafts: evt.pending_drafts || [],
      breakdown: evt.breakdown,
      date_field: evt.date_field,
      // a term asked back (#1665): what the card asks, the meanings to choose from, where the draft may belong
      ...(evt.term_check ? {
        perspective_options: evt.perspective_options || [],
        meanings: evt.meanings || [],
        question: evt.question || [],
        english: evt.english ?? null,
        proposal: evt.proposal ?? null,
        error: evt.error ?? null,
        asked_question: evt.asked_question ?? null,
        term_check: evt.term_check,
      } : {}),
    };
    patch.landingAgentDefinitionRequest = request;
    turnPatch.status = 'defining';
    turnPatch.definitionRequest = request;
  } else if (evt.type === 'formatter_result' && evt.status === 'done') {
    const existing = conv.result || {};
    const formatters = [...(existing.formatterResults || []).filter((f: any) => f.sub_task_id !== evt.sub_task_id), evt];
    patch.landingAgentResult = {
      ...existing, formatterResults: formatters,
      formatterSummary: formatters.map(f => f.summary).filter(Boolean).join('\n\n'),
      formatterHighlights: formatters.flatMap(f => f.highlights || []),
      formatterTitle: evt.chart_title,
      bundles: formatters.flatMap(f => f.bundles || []),
    };
    turnPatch.result = patch.landingAgentResult;
  } else if (evt.type === 'synthesis_result') {
    const existing = conv.result || {};
    patch.landingAgentResult = { ...existing, synthesis: evt.synthesis };
    turnPatch.result = patch.landingAgentResult;
  } else if (evt.type === 'ladder') {
    turnPatch.analysisAwaitingLadder = false;
    // keep the envelope on the result for everything that will consume claims
    // (#183 popovers, pins, reviews) — no render change. A Why stores it before its terminal event.
    const existing = conv.result || {};
    if ((existing.ladder?.delivery?.revision || 0) > (evt.ladder?.delivery?.revision || 0)) return null;
    patch.landingAgentResult = evt.ladder?.status === 'declined'
      ? { ...withoutRows(existing), ladder: evt.ladder, mission_status: 'declined' }
      : { ...existing, ladder: evt.ladder };
    turnPatch.result = patch.landingAgentResult;
    // a catalog answer (#761) has no synthesis: its headline is the reply the conversation remembers
    // a declined answer (#1174) likewise: the decline sentence is the reply, its ladder arriving after its end
    const catalogReply = evt.ladder?.catalog || evt.ladder?.status === 'declined'
      ? String(evt.ladder?.what?.headline || '').slice(0, 400) : '';
    if (catalogReply) {
      patch.landingAgentHistory = placeHistoryReply(conv.history, turn.id,
        historyText(catalogReply, patch.landingAgentResult), turn.foreground !== false);
    }
    // the answer's scope is the thread's context now — when the caller accepts it
    const ctx = evt.ladder?.thread_context;
    if (ctx && acceptsContext(ctx)) patch.landingAgentThreadContext = ctx;
  } else if (evt.type === 'diagnose_result') {
    if (Array.isArray(evt.inherited) && evt.inherited.length) turnPatch.inherited = evt.inherited;
    // deterministic why-engine answer (#139): single terminal event.
    // The walk is a traced session (#184): its id rides the event, so
    // the Trace button and "Open full trace" open the same session.
    patch.landingAgentStatus = 'done';
    turnPatch.status = 'done';
    if (evt.session_id) turnPatch.sessionId = evt.session_id;
    const existing = conv.result || {};
    turnPatch.analysisAwaitingLadder = !existing.ladder;
    patch.landingAgentResult = {
      ...existing,
      // a loop's words about the walk are the answer's, whichever frame came first (#2203)
      synthesis: existing.synthesis?.loop ? existing.synthesis : { headline: evt.synthesis },
      diagnose: evt.diagnose,
    };
    turnPatch.result = patch.landingAgentResult;
    const headline = (evt.diagnose?.verdict?.headline || evt.synthesis || '')
      .toString().slice(0, 400);
    if (headline) {
      patch.landingAgentHistory = placeHistoryReply(conv.history, turn.id,
        historyText(headline, patch.landingAgentResult), turn.foreground !== false);
    }
  } else if (evt.type === 'prior_work_offered') {
    // an earlier analysis, offered before Why or Data (#762): the turn holds the offer; nothing else ran
    turnPatch.priorWork = { ...evt, type: 'prior_work_offered', matches: Array.isArray(evt.matches) ? evt.matches : [] };
    if (evt.session_id) turnPatch.sessionId = evt.session_id;
  } else if (evt.type === 'prior_work_refreshed') {
    // a Refresh (#762), or a repeat answered from its earlier analysis (#911): then vs now, beside the answer it produced
    turnPatch.priorWork = { ...evt, type: 'prior_work_refreshed', source: evt.source || null };
  } else if (evt.type === 'prior_work_related') {
    // the earlier analyses an answer that ran fresh names (#911 part C), beside it
    turnPatch.priorWork = { ...evt, type: 'prior_work_related', lines: Array.isArray(evt.lines) ? evt.lines : [] };
  } else if (evt.type === 'mission_error') {
    turnPatch.analysisAwaitingLadder = false;
    patch.landingAgentStatus = 'error';
    patch.landingAgentError = evt.error || 'Unknown error';
    turnPatch.status = 'error';
    turnPatch.error = patch.landingAgentError;
    turnPatch.errorStatus = typeof evt.status === 'number' ? evt.status : null;
    // #2049: a planning failure names its traced session, so the error has a Trace link
    if (evt.session_id) turnPatch.sessionId = evt.session_id;
  } else if (evt.type === 'mission_cancelled') {
    turnPatch.analysisAwaitingLadder = false;
    patch.landingAgentStatus = 'cancelled';
    turnPatch.status = 'cancelled';
  } else if (evt.type === 'mission_complete') {
    // 'defining' joins 'clarifying' here: the mission completed by
    // stopping to ask for something, so overwriting the status with
    // 'done' would retire the card the user still has to act on.
    const held = conv.status;
    if (held !== 'clarifying' && held !== 'defining' && held !== 'cancelled') {
      patch.landingAgentStatus = 'done';
      turnPatch.status = 'done';
    } else {
      turnPatch.status = held;
    }
    const existing = conv.result || {};
    const links = confirmLinksOf(evt.confirm_for_everyone);
    if (links.length) turnPatch.confirmForEveryone = links;
    if (evt.mission_status) {
      patch.landingAgentResult = { ...(evt.mission_status === 'declined' ? withoutRows(existing) : existing),
        mission_status: evt.mission_status };
      turnPatch.result = patch.landingAgentResult;
    }
    const res = patch.landingAgentResult || existing;
    const assistantContent = (res.synthesis?.headline
      || (res.ladder?.specialist || res.ladder?.prior_work ? res.ladder?.what?.headline : null)
      || res.formatterTitle
      || res.formatterSummary
      || '').toString().slice(0, 400);
    if (assistantContent) {
      patch.landingAgentHistory = placeHistoryReply(conv.history, turn.id,
        historyText(assistantContent, res), turn.foreground !== false);
    }
  }
  return { patch, turnPatch };
}

/**
 * A turn the answer gate held (#1174 §5): the server rebuilt its answer from the trusted public trace and sends it as
 * frames (`gated: true`), never the client's copy. Painted here through the socket's own reducer, as a restored
 * answer is; any other turn is returned as it is.
 */
export function paintServerTurn(turn: LandingAgentTurn & { gated?: boolean }): LandingAgentTurn {
  // the titles-only list (#1596) carries a turn's question only: its answer is the open's (`GET /api/threads/{id}`)
  if (turn && turn.listed === true) {
    return { ...turn, status: 'done', result: null, events: [], clarification: null, definitionRequest: null, error: null,
      answerPending: true };
  }
  if (!turn || !turn.gated || turn.savedAsShown) return turn;     // an answer saved as shown is not rebuilt from its frames
  // the thread list carries a held turn's question only (#1270): its answer is the open's (`GET /api/threads/{id}`)
  if (!Array.isArray(turn.events)) {
    return { ...turn, status: 'done', result: null, events: [], clarification: null, definitionRequest: null, error: null,
      answerPending: true };
  }
  let painted: LandingAgentTurn = { ...turn, status: 'running', result: null, events: [], clarification: null,
    definitionRequest: null, error: null };
  let conv: TurnConversation = { events: [], status: 'running', result: null, history: [] };
  for (const evt of (Array.isArray(turn.events) ? turn.events : [])) {
    const reduced = reduceTurnEvent(conv, painted, evt, () => false);
    if (!reduced) continue;
    painted = { ...painted, ...reduced.turnPatch };
    conv = { events: reduced.patch.landingAgentEvents ?? conv.events, status: reduced.patch.landingAgentStatus ?? conv.status,
      result: reduced.patch.landingAgentResult !== undefined ? reduced.patch.landingAgentResult : conv.result,
      history: reduced.patch.landingAgentHistory ?? conv.history };
  }
  return painted;
}

const activeDiagnoses = new Set<string>();

function runWhyTurn(
  get: () => VoyagerState,
  set: (patch: Partial<VoyagerState>) => void,
  question: string,
  run: (threadId: string, options: DiagnosisRunOptions) => Promise<DiagnoseEnvelope>,
  replaceTurnId?: string,
  perspectiveId?: string | null,
  intent?: 'why' | 'next',
  resume = false,
  parentTurnId?: string,
) {
  let threadId = (get() as any).currentThreadId as string | null;
  if (!threadId) {
    threadId = generateId();
    set({ currentThreadId: threadId } as any);
  }
  const tid = threadId;
  const workspaceGeneration = get().threadsGeneration;
  const onScreen = () => (get() as any).currentThreadId === tid;
  const persistThread = () => {
    const persist = (get() as any).persistCurrentThread;
    if (typeof persist === 'function') persist(tid);
  };

  const turns = get().landingAgentTurns;
  const existing = replaceTurnId ? turns.find((t) => t.id === replaceTurnId) : undefined;
  const operationId = (resume && existing?.diagnosisOperationId) || generateId();
  const trackerKey = `${workspaceGeneration}:${operationId}`;
  if (activeDiagnoses.has(trackerKey)) return;
  activeDiagnoses.add(trackerKey);
  if (!resume && existing?.analysisOperationId) detachBackgroundSockets([existing]);
  const activeSocket = get().landingAgentWs;
  if (!resume && activeSocket && !Array.from(backgroundSockets.values()).includes(activeSocket)) closeAgentWs(activeSocket, tid);
  const myGen = threadGen(tid);
  const turnId = existing ? existing.id : `t_${generateId()}`;
  const ownsForeground = () => {
    const currentTurns = onScreen() ? get().landingAgentTurns : get().parkedConversations[tid]?.turns || [];
    const foreground = currentTurns.find(t => t.foreground) || currentTurns[currentTurns.length - 1];
    return myGen === threadGen(tid) && foreground?.id === turnId;
  };
  const running: LandingAgentTurn = existing
    ? { ...existing, status: resume && existing.diagnosisCancelPending ? 'cancelled' : 'running', result: null, error: null, stopped: !!(resume && existing.diagnosisCancelPending), isWalk: true }
    : { id: turnId, userText: question, sentAt: Date.now(), status: 'running', result: null,
        events: [], clarification: null, definitionRequest: null, error: null, isWalk: true,
        perspectiveId: perspectiveId ?? (get().selectedPerspectiveId || null), ...(intent ? { intent } : {}) };
  if (!resume) {
    running.foreground = true;
    running.analysisOperationId = undefined;
    running.analysisSequence = undefined;
    running.analysisDisconnected = undefined;
    running.analysisCancelPending = undefined;
    running.analysisAwaitingLadder = undefined;
    running.diagnosisPreviousOperationId = existing?.diagnosisOperationId;
    running.diagnosisPreviousOperationIds = existing ? [...(existing.diagnosisPreviousOperationIds || []), existing.diagnosisOperationId || null] : [];
  }
  if (parentTurnId) running.diagnosisParentTurnId = parentTurnId;
  running.diagnosisOperationId = operationId;
  running.diagnosisDisconnected = false;
  if (!resume) { running.diagnosisCancelPending = false; running.diagnosisProgress = undefined; running.sessionId = null; }
  set(resume ? { landingAgentTurns: turns.map(t => t.id === turnId ? running : t) } : {
    landingAgentTurns: existing ? settleSuperseded(turns).map((t) => (t.id === turnId ? running : t)) : [...settleSuperseded(turns), running],
    landingAgentMessages: existing ? get().landingAgentMessages
      : [...get().landingAgentMessages, { id: `u_${generateId()}`, role: 'user', text: question, at: Date.now() }],
    landingAgentHistory: existing ? get().landingAgentHistory
      : [...get().landingAgentHistory, { role: 'user' as const, content: question, turnId }].slice(-12),
    landingAgentEvents: [],
    landingAgentStatus: 'running',
    landingAgentResult: null,
    landingAgentClarification: null,
    landingAgentDefinitionRequest: null,
    landingAgentError: null,
  });
  persistThread();

  const land = (turnPatch: Partial<LandingAgentTurn>, status: LandingAgentStatus,
                result: LandingAgentResult | null, error: string | null, headline: string | null) => {
    // Stop wins over a late answer: a turn the person stopped stays stopped.
    const current = (onScreen() ? get().landingAgentTurns : (get().parkedConversations[tid]?.turns || [])).find((t) => t.id === turnId);
    if (!current || current.stopped || current.diagnosisOperationId !== operationId || get().threadsGeneration !== workspaceGeneration) return;
    const apply = (ts: LandingAgentTurn[]) => ts.map((t) => (t.id === turnId ? { ...t, ...turnPatch, status } : t));
    // The reply lands under ITS question, found by turn id — never by text,
    // since the same question can be asked twice. An in-place rerun
    // replaces the turn's earlier reply; a late reply is inserted after its
    // question; a question that already fell off the transcript gets nothing.
    const placeReply = (h: HistoryTurn[], appendIfUnplaced: boolean) => placeHistoryReply(h, turnId, headline || '', appendIfUnplaced);
    const withReply = (h: HistoryTurn[]) => placeReply(h, true);
    if (!ownsForeground()) {
      // A newer question in this thread owns the screen now. The turn still
      // gets its answer — a card that says "Thinking…" forever is the bug
      // #167 fixed — and the history gets the reply under ITS question, so
      // the transcript matches the thread; the screen-level status and
      // result belong to the newer question and stay untouched.
      const lateReply = (h: HistoryTurn[]) => placeReply(h, false);
      if (onScreen()) {
        set({ landingAgentTurns: apply(get().landingAgentTurns), landingAgentHistory: lateReply(get().landingAgentHistory) });
      } else {
        const parked = get().parkedConversations[tid];
        if (parked) set({ parkedConversations: { ...get().parkedConversations, [tid]: { ...parked, turns: apply(parked.turns), history: lateReply(parked.history) } } });
      }
      persistThread();
      return;
    }
    if (onScreen()) {
      set({
        landingAgentTurns: apply(get().landingAgentTurns), landingAgentStatus: status,
        landingAgentResult: result, landingAgentError: error,
        landingAgentHistory: withReply(get().landingAgentHistory),
      });
    } else {
      const parked = get().parkedConversations[tid];
      if (!parked) return; // closed or deleted meanwhile: nothing to land on
      set({ parkedConversations: { ...get().parkedConversations, [tid]: {
        ...parked, turns: apply(parked.turns), status, result, error, history: withReply(parked.history), unread: true,
      } } });
    }
    persistThread();
  };

  const currentTurn = () => (onScreen() ? get().landingAgentTurns : get().parkedConversations[tid]?.turns || []).find(t => t.id === turnId);
  const options: DiagnosisRunOptions = {
    operationId,
    isCurrent: () => get().threadsGeneration === workspaceGeneration && !!currentTurn(),
    shouldStop: () => !!currentTurn()?.stopped || (!!currentTurn() && currentTurn()?.diagnosisOperationId !== operationId),
    onProgress: progress => {
      const current = currentTurn();
      if (!current || (current.stopped && !current.diagnosisCancelPending) || current.diagnosisOperationId !== operationId || get().threadsGeneration !== workspaceGeneration) return;
      // Results are stored through land, not copied into the status snapshot.
      const { id, status: runStatus, session_id, queries_started, queries_completed, message } = progress;
      const status: DiagnosisRun = { id, status: runStatus, session_id, queries_started, queries_completed, message };
      if (JSON.stringify(current.diagnosisProgress) === JSON.stringify(status)) return;
      // A run that finished before the Stop reached it keeps its answer: the turn is live again, so it lands.
      const finishedFirst = !!current.stopped && progress.status === 'done';
      const apply = (ts: LandingAgentTurn[]) => ts.map(t => t.id === turnId ? { ...t, diagnosisProgress: status, sessionId: progress.session_id,
        diagnosisCancelPending: !!current.diagnosisCancelPending && ['queued', 'running'].includes(progress.status),
        ...(finishedFirst ? { stopped: false, status: 'running' as const } : {}) } : t);
      if (onScreen()) set({ landingAgentTurns: apply(get().landingAgentTurns) });
      else {
        const parked = get().parkedConversations[tid];
        if (parked) set({ parkedConversations: { ...get().parkedConversations, [tid]: { ...parked, turns: apply(parked.turns) } } });
      }
      persistThread();
    },
  };
  run(tid, options).then((envelope) => {
    const headline = String(envelope?.verdict?.headline || '').slice(0, 400);
    // The route answers with the walk AND its ladder (WHAT NEXT rests on the
    // walk's drivers): the ladder is kept on the result the way the socket
    // path keeps it, so the WHAT NEXT rung has its suggestions.
    const { ladder, ...walk } = (envelope || {}) as any;
    const result: LandingAgentResult = { synthesis: { headline }, diagnose: walk, ...(ladder ? { ladder } : {}) };
    land({ result, sessionId: envelope?.session_id || null, error: null }, 'done', result, null, historyText(headline, result) || null);
    // the walk's target is what the thread is about now
    const t = envelope?.target;
    if (t?.metric && ownsForeground() && get().threadsGeneration === workspaceGeneration && !currentTurn()?.stopped && currentTurn()?.diagnosisOperationId === operationId) {
      const ctx: ThreadContext = { perspective: perspectiveId ?? get().selectedPerspectiveId ?? null, metrics: [t.metric], primary_metric: t.metric, dimensions: [],
        filters: Array.isArray(t.filters) ? t.filters.filter((f: any) => f && typeof f === 'object') : [],
        window: t.window_start && t.window_end ? { start: String(t.window_start).slice(0, 10), end: inclusiveEnd(t.window_end) } : null,
        definitions: [], session_id: envelope?.session_id || null };
      if (onScreen()) { if (!ctx.perspective || ctx.perspective === get().selectedPerspectiveId || get().selectedPerspectiveId === 'auto') set({ landingAgentThreadContext: ctx } as any); }
      else {
        const parked = get().parkedConversations[tid];
        if (parked) set({ parkedConversations: { ...get().parkedConversations, [tid]: { ...parked, threadContext: ctx } } });
      }
    }
  }).catch((e: any) => {
    const msg = e?.response?.data?.error || e?.message || 'The walk failed.';
    if (currentTurn()?.diagnosisCancelPending && get().threadsGeneration === workspaceGeneration) {
      const apply = (ts: LandingAgentTurn[]) => ts.map(t => t.id === turnId && t.diagnosisOperationId === operationId ? { ...t, diagnosisDisconnected: true, error: msg } : t);
      if (onScreen()) set({ landingAgentTurns: apply(get().landingAgentTurns) });
      else {
        const parked = get().parkedConversations[tid];
        if (parked) set({ parkedConversations: { ...get().parkedConversations, [tid]: { ...parked, turns: apply(parked.turns) } } });
      }
      persistThread();
    } else land({ error: msg, diagnosisDisconnected: e?.reconnectableDiagnosis === true }, 'error', null, msg, null);
  }).finally(() => activeDiagnoses.delete(trackerKey));
}

export const createLandingAgentSlice: StateCreator<
  VoyagerState, [], [], LandingAgentSlice
> = (set, get) => ({
  landingAgentDomains: [],
  parkedConversations: {},
  resolveWait: (turnId) => {
    const mark = (turns: LandingAgentTurn[]) => turns.map((t) => (t.id === turnId && !t.waitResolved ? { ...t, waitResolved: true } : t));
    const persist = (get() as any).persistCurrentThread as ((id?: string) => void) | undefined;
    if (get().landingAgentTurns.some((t) => t.id === turnId)) {
      set({ landingAgentTurns: mark(get().landingAgentTurns) });
      persist?.();
      return;
    }
    const threadId = Object.keys(get().parkedConversations).find((id) => get().parkedConversations[id].turns.some((t) => t.id === turnId));
    if (!threadId) return;
    const parked = get().parkedConversations[threadId];
    set({ parkedConversations: { ...get().parkedConversations, [threadId]: { ...parked, turns: mark(parked.turns) } } });
    persist?.(threadId);
  },
  selectedLandingAgentDomainId: null,
  perspectivesList: [],
  selectedPerspectiveId: null,
  landingAgentMode: 'fast',
  landingAgentMessages: [],
  landingAgentEvents: [],
  landingAgentStatus: 'idle',
  landingAgentResult: null,
  landingAgentClarification: null,
  landingAgentDefinitionRequest: null,
  landingAgentError: null,
  landingAgentWs: null,
  landingAgentHistory: [],
  landingAgentTurns: [],
  landingAgentThreadContext: null,
  verdictPanel: null,
  openVerdictPanel: (panel) => set({ verdictPanel: panel }),
  closeVerdictPanel: () => set({ verdictPanel: null }),
  answerSupportingOpen: {},
  setAnswerSupportingOpen: (turnId, open) =>
    set((state) => ({ answerSupportingOpen: { ...state.answerSupportingOpen, [turnId]: open } })),

  fetchLandingAgentPerspectives: async () => {
    try {
      // Without the domain layer (#464) there is nothing to pick a domain from:
      // the chip lists every perspective.
      const { domains: domainsOn } = await get().ensureFeatures();
      const [list, domains] = await Promise.all([getAllPerspectives(), domainsOn ? getDomains() : Promise.resolve([])]);
      const current = get();
      // Default to the first domain (force a pick rather than 'All domains').
      const domainId = domainsOn ? (current.selectedLandingAgentDomainId || (domains[0]?.id ?? null)) : null;
      const scoped = domainId ? list.filter((p) => p.domain === domainId) : list;
      const currentValid = scoped.some((p) => p.id === current.selectedPerspectiveId);
      set({
        perspectivesList: list,
        landingAgentDomains: domains,
        selectedLandingAgentDomainId: domainId,
        selectedPerspectiveId: currentValid
          ? current.selectedPerspectiveId
          : (scoped.length ? 'auto' : null),
      });
    } catch (e: any) {
      set({ landingAgentError: `Failed to load perspectives: ${e?.message || e}` });
    }
  },

  setSelectedLandingAgentDomain: (id: string | null) => {
    const { perspectivesList, selectedPerspectiveId } = get();
    const scoped = id ? perspectivesList.filter((p) => p.domain === id) : perspectivesList;
    const stillValid = scoped.some((p) => p.id === selectedPerspectiveId);
    const nextPerspective = stillValid ? selectedPerspectiveId : (scoped.length ? 'auto' : null);
    set({
      selectedLandingAgentDomainId: id,
      selectedPerspectiveId: nextPerspective,
      ...(nextPerspective !== selectedPerspectiveId ? { landingAgentThreadContext: contextForPerspective(get().landingAgentThreadContext, nextPerspective) } : {}),
    });
  },

  // A perspective switch keeps the conversation — the transcript and the
  // window still hold — and drops what cannot cross a perspective: the
  // metrics, groupings, filters and definitions of the previous one.
  setSelectedPerspective: (id: string) => set({
    selectedPerspectiveId: id,
    landingAgentThreadContext: contextForPerspective(get().landingAgentThreadContext, id),
  }),
  setLandingAgentMode: (m: LandingAgentMode) => set({ landingAgentMode: m }),

  pickPerspective: (id: string) => {
    const p = get().perspectivesList.find((x) => x.id === id);
    if (get().features?.domains && p?.domain && p.domain !== get().selectedLandingAgentDomainId) {
      set({ selectedLandingAgentDomainId: p.domain });
    }
    get().setSelectedPerspective(id);
  },

  reaskInPerspective: (turn, perspectiveId, scopeFor, opts) => {
    const reask = turn.askedRequest;
    if (!reask) return false;
    if (scopeFor === 'thread') get().pickPerspective(perspectiveId);
    // F4 (#1858 §7): a change made while the turn ran re-asks in its place — a Change of the turn's own route naming
    // the turn it replaces, the one shape the server records (`earlier.note_change`) and acknowledges with `replaced`
    const inPlace = opts?.replaces && opts.route ? { replaces: opts.replaces, route: opts.route, routeFrom: opts.route } : {};
    return get().sendLandingAgentQuery(reask.query, { reask, perspectiveId, scopeFrom: reask.perspective_id, scopeFor,
      ...inPlace });
  },

  sendLandingAgentQuery: (text: string, opts?: { resumeTurnId?: string; threadContext?: ThreadContext | null; useDefinitions?: string[]; source?: { kind: string; id: string } | null; route?: string; routeFrom?: string | null; reask?: AskRequest | null; perspectiveId?: string; scopeFrom?: string | null; scopeFor?: 'question' | 'thread'; attachment?: Attachment | null; prior?: PriorChoice; answers?: string | null; choice?: ReadingChoice | null; earlier?: EarlierChoice; replaces?: string | null; defaultChange?: DefaultChange | null }) => {
    const resumed = opts?.resumeTurnId ? get().landingAgentTurns.find(t => t.id === opts.resumeTurnId && t.analysisOperationId) : undefined;
    if (opts?.resumeTurnId && (!resumed || resumed.stopped || (resumed?.analysisOperationId && backgroundSockets.get(resumed.analysisOperationId) && backgroundSockets.get(resumed.analysisOperationId)!.readyState < WebSocket.CLOSING))) return false;
    // A re-ask (#760) sends the original request's words, scope and opt-ins, never the thread's current ones; so does
    // a resumed turn (#767: its own perspective, not whatever the thread is on now).
    const reask = opts?.reask || resumed?.askedRequest || null;
    // A perspective for THIS question only (#767: a number's own, Use Y, a follow-up bound to its answer) never touches
    // the pick; naming the pick itself is just the pick.
    const perQuestion = opts?.perspectiveId && (reask || opts.perspectiveId !== get().selectedPerspectiveId) ? opts.perspectiveId : undefined;
    const q = (reask ? reask.query : text).trim();
    const useDefinitions = reask ? [...reask.use_definitions] : (opts?.useDefinitions || []).filter(Boolean);
    // Returns whether the question STARTED. A composer that clears the box on a
    // refusal loses what someone typed and shows nothing for it — the question
    // simply vanishes, which reads as broken rather than as a prompt.
    if (!q) return false;
    // the thread's earlier answers are still being filled from its own read (#1270): a question asked now would go
    // without their context, so it waits (the composer says so)
    const openThread = (get() as any).currentThreadId as string | null;
    if (openThread && (get() as any).threadAnswers?.[openThread] === 'loading') return false;
    const { landingAgentMode, landingAgentWs, landingAgentHistory } = get();
    const selectedPerspectiveId = perQuestion || (reask ? reask.perspective_id : get().selectedPerspectiveId);
    const submittedDomainId = perQuestion ? null : reask ? reask.domain_id : (get().features?.domains ? get().selectedLandingAgentDomainId : null);
    if (!selectedPerspectiveId) {
      set({ landingAgentError: 'Select a perspective first' });
      return false;
    }
    // stamped only once the question is really starting — a refused one must not mark the next thread
    leaveReviewOnScreen(get, set, opts?.source);
    // The thread's context is read AFTER a review was left: a question opened
    // from a review mints its own thread and starts with no scope — never the
    // previous conversation's metric, filters and window.
    const landingAgentThreadContext = get().landingAgentThreadContext;
    let threadId = (get() as any).currentThreadId as string | null;
    if (!threadId) {
      threadId = generateId();
      set({ currentThreadId: threadId } as any);
    }
    // A new question replaces the run in THIS thread only.
    const previousTurn = get().landingAgentTurns.find(t => t.foreground) || get().landingAgentTurns[get().landingAgentTurns.length - 1];
    // A socket a background question still owns (kept on screen by a Why turn) is not this thread's to close.
    const ownedElsewhere = !!landingAgentWs && Array.from(backgroundSockets.values()).includes(landingAgentWs);
    if (!resumed && !previousTurn?.analysisOperationId) closeAgentWs(ownedElsewhere ? null : landingAgentWs, threadId);
    const myThreadGen = threadGen(threadId);
    // The question's request, built once: it goes on the wire as is and stays on the turn (#760). The thread is
    // where this question is answered — a re-ask lives in the conversation it is made in.
    const askedRequest: AskRequest = reask ? { ...reask, thread_id: threadId,
      ...(perQuestion ? { perspective_id: perQuestion, domain_id: null } : {}) } : {
      query: q,
      perspective_id: selectedPerspectiveId,
      domain_id: submittedDomainId,
      mode: landingAgentMode,
      thread_id: threadId,
      history: landingAgentHistory.slice(-6).map(({ role, content }) => ({ role, content })),
      // What the last answer was scoped to, as the server wrote it: the
      // follow-up inherits what it omits. Nothing here bypasses validation.
      thread_context: opts && 'threadContext' in opts ? opts.threadContext ?? null : landingAgentThreadContext,
      // Ids only — the server reads the formula from the store under the
      // caller's own scope, so this cannot widen what they may see.
      use_definitions: useDefinitions,
      // the objective this thread is asked inside (#766): the stored thread's, or a new one's pending objective
      objective_id: (() => {
        const st = get() as any;
        const stored = (st.savedThreads || []).find((t: any) => t.id === threadId);
        return stored ? (stored.objectiveId ?? null) : (st.pendingThreadObjective ?? null);
      })(),
      attachment: opts?.attachment || null,
    };

    /**
     * Everything the socket does goes through these two, so events keep
     * landing on the right thread after the person switched tabs: on screen
     * → the active fields; parked → that thread's parked conversation.
     */
    const onScreen = () => (get() as any).currentThreadId === threadId;
    // Every way a turn ends — answer, error, cancel, a socket that closed —
    // reaches the durable snapshot, named by thread so a background tab's
    // ending is stored too. Otherwise a reload would restore a turn that
    // is still "running" forever.
    const persistThread = () => {
      const persist = (get() as any).persistCurrentThread;
      if (typeof persist === 'function') persist(threadId);
    };
    const ownerView = (conv: Pick<ParkedConversation, 'turns' | 'history' | 'events' | 'status' | 'result' | 'error' | 'threadContext'>) => {
      const owner = conv.turns.find((t: LandingAgentTurn) => t.id === turnId);
      return owner ? { ...conv, result: owner.result, events: owner.events, status: owner.status, error: owner.error } : conv;
    };
    const readConv = () => {
      if (onScreen()) {
        const st = get();
        return ownerView({
          turns: st.landingAgentTurns, history: st.landingAgentHistory, events: st.landingAgentEvents,
          status: st.landingAgentStatus, result: st.landingAgentResult, error: st.landingAgentError,
          threadContext: st.landingAgentThreadContext,
        });
      }
      const parked = get().parkedConversations[threadId as string];
      return ownerView({
        turns: parked?.turns || [], history: parked?.history || [], events: parked?.events || [],
        status: parked?.status || 'idle', result: parked?.result || null, error: parked?.error || null,
        threadContext: parked?.threadContext ?? null,
      });
    };
    const writeConv = (patch: Partial<LandingAgentSlice>, terminal = false) => {
      const turns = onScreen() ? get().landingAgentTurns : get().parkedConversations[threadId as string]?.turns || [];
      const owner = turns.find(t => t.id === turnId);
      if (owner && owner.foreground === false) {
        patch = {
          ...(patch.landingAgentTurns !== undefined ? { landingAgentTurns: patch.landingAgentTurns } : {}),
          ...(patch.landingAgentHistory !== undefined ? { landingAgentHistory: patch.landingAgentHistory } : {}),
        };
      }
      if (onScreen()) { set(patch as any); return; }
      const parked = get().parkedConversations[threadId as string];
      if (!parked) return; // closed or deleted meanwhile: nothing to land on
      const next: ParkedConversation = {
        ...parked,
        ...(patch.landingAgentTurns !== undefined ? { turns: patch.landingAgentTurns } : {}),
        ...(patch.landingAgentHistory !== undefined ? { history: patch.landingAgentHistory } : {}),
        ...(patch.landingAgentThreadContext !== undefined ? { threadContext: patch.landingAgentThreadContext } : {}),
        ...(patch.landingAgentEvents !== undefined ? { events: patch.landingAgentEvents } : {}),
        ...(patch.landingAgentStatus !== undefined ? { status: patch.landingAgentStatus } : {}),
        ...(patch.landingAgentResult !== undefined ? { result: patch.landingAgentResult } : {}),
        ...(patch.landingAgentClarification !== undefined ? { clarification: patch.landingAgentClarification } : {}),
        ...(patch.landingAgentDefinitionRequest !== undefined ? { definitionRequest: patch.landingAgentDefinitionRequest } : {}),
        ...(patch.landingAgentError !== undefined ? { error: patch.landingAgentError } : {}),
        ...(patch.landingAgentWs !== undefined ? { ws: patch.landingAgentWs } : {}),
        ...(terminal ? { unread: true } : {}),
      };
      set({ parkedConversations: { ...get().parkedConversations, [threadId as string]: next } });
    };
    const turnId = resumed?.id || generateId();
    // a question asked here is watched here (§5); re-attaching to one is not watching it from the start
    if (!resumed) liveThisPage.add(turnId);
    // A changed pick is a trace event (#767): named by the re-ask that changed it, or, for a question typed after
    // the asker changed the picker, the thread's pick when the previous question was sent.
    const threadPick = get().selectedPerspectiveId;
    const previousPick = [...get().landingAgentTurns].reverse().find((t) => t.threadPick)?.threadPick;
    const scopeFrom = opts?.scopeFor ? (opts.scopeFrom ?? null)
      : (!reask && !perQuestion && previousPick && previousPick !== selectedPerspectiveId ? previousPick : null);
    const scopeFor = opts?.scopeFor || (scopeFrom ? 'thread' : null);
    const userMsg: LandingAgentMessage = {
      id: `u_${Date.now()}`,
      role: 'user',
      text: q,
      at: Date.now(),
    };
    const newTurn: LandingAgentTurn = {
      id: turnId,
      foreground: true,
      shownLive: true,
      userText: q,
      sentAt: Date.now(),
      status: 'running',
      result: null,
      events: [],
      clarification: null,
      definitionRequest: null,
      error: null,
      perspectiveId: selectedPerspectiveId,
      threadPick,
      askedRequest,
      analysisOperationId: resumed?.analysisOperationId || generateId(),
      // C8 (#1672): a new socket expects no part — what an earlier one streamed comes whole with its final frame
      ...(resumed ? { ...withoutStreaming(resumed), analysisDisconnected: false, error: null } : {}),
    };
    writeConv({
      landingAgentMessages: resumed ? get().landingAgentMessages : [...get().landingAgentMessages, userMsg],
      landingAgentHistory: resumed ? landingAgentHistory : [...landingAgentHistory, { role: 'user' as const, content: q, turnId }].slice(-12),
      landingAgentTurns: resumed ? get().landingAgentTurns.map(t => t.id === turnId ? newTurn : t) : [...settleSuperseded(get().landingAgentTurns), newTurn],
      landingAgentEvents: resumed?.events || [],
      landingAgentStatus: resumed?.status || 'running',
      landingAgentResult: resumed?.result || null,
      landingAgentClarification: resumed?.clarification || null,
      landingAgentDefinitionRequest: resumed?.definitionRequest || null,
      landingAgentError: null,
    });
    // The thread exists from the first question — as a tab you can star or
    // close while the run is still going — not from the first answer.
    {
      const persist = (get() as any).persistCurrentThread;
      if (typeof persist === 'function') persist(threadId);
    }

    // Fail the in-flight turn. A turn that already reached a card or an answer
    // keeps it — a socket dropping afterwards is not what the user is looking
    // at. Both the turn AND the global status move, so nothing is left holding
    // a spinner over a mission that will never report.
    const failTurn = (message: string) => {
      const turns = readConv().turns;
      const lastIdx = turns.findIndex((t: LandingAgentTurn) => t.id === turnId);
      if (lastIdx < 0) return;
      // A resumed question, or one that may be running on the server, stays reconnectable when it cannot be reached —
      // never failed: its answer still arrives, and asking again would run it twice.
      if (turns[lastIdx].analysisAwaitingLadder || ((resumed || mayBeRunning) && turns[lastIdx].status === 'running')) {
        writeConv({ landingAgentTurns: turns.map((t, i) => i === lastIdx ? { ...t, analysisDisconnected: true, error: message } : t) });
        persistThread();
        return;
      }
      if (turns[lastIdx].status !== 'running') return;
      writeConv({
        landingAgentStatus: 'error',
        landingAgentError: message,
        landingAgentTurns: turns.map((t, i) => (
          i === lastIdx ? { ...t, status: 'error' as const, error: message } : t
        )),
      }, true);
      persistThread();
    };

    // Settle a turn whose socket closed without a terminal event. Keyed on the
    // TURN's own status, not the global one: onerror fires first and moves the
    // global status off 'running', which is what used to make this skip the
    // turn and leave it spinning on "Thinking…" forever.
    const finishTurn = () => {
      const turns = readConv().turns;
      const lastIdx = turns.findIndex((t: LandingAgentTurn) => t.id === turnId);
      if (lastIdx < 0 || turns[lastIdx].status !== 'running') return;
      const patch: Partial<LandingAgentSlice> = {
        landingAgentTurns: turns.map((t, i) => (
          i === lastIdx ? { ...t, status: 'done' as const } : t
        )),
      };
      if (readConv().status === 'running') patch.landingAgentStatus = 'done';
      writeConv(patch, true);
      persistThread();
    };

    // Whether this question ever heard back. It is the only honest signal that
    // the mission actually started: a handshake the backend rejects is ACCEPTED
    // first (that is the only way a browser ever sees the 4401 close code), so
    // `onopen` fires even for a socket that carried nothing at all.
    let progressed = false;
    // A question that left and then lost its connection abnormally may be running on the server even though nothing
    // came back. A refused handshake or a clean close means the server ended it without starting a run.
    let mayBeRunning = false;

    const sentReplaces = opts?.replaces || null;
    let replacedApplied = false;
    const handleMessage = (msg: MessageEvent) => {
      let evt: LandingAgentEvent;
      try { evt = JSON.parse(msg.data); } catch {
        console.warn('[agent-ws] non-JSON message', msg.data);
        return;
      }
      const owningTurn = readConv().turns.find(t => t.id === turnId);
      if (!owningTurn || owningTurn.analysisOperationId !== newTurn.analysisOperationId || (owningTurn.stopped && evt.type !== 'mission_cancelled') || (evt.run_seq && evt.run_seq <= (owningTurn.analysisSequence || 0))) return;
      progressed = true;
      // #1807 (F2 §1.2): a Change replaces its turn in place exactly when the server says it recorded it — the first
      // frame (and any error) carries `replaced`, the session sent; anything else keeps the old turn
      if (sentReplaces && !replacedApplied && (evt as any).replaced === sentReplaces) {
        replacedApplied = true;
        const turns = readConv().turns;
        const oldIdx = turns.findIndex((t) => t.sessionId === sentReplaces && t.id !== turnId);
        const mine = turns.find((t) => t.id === turnId);
        if (oldIdx >= 0 && mine) {
          const next = turns.filter((t) => t.id !== turnId);
          next.splice(next.findIndex((t) => t.sessionId === sentReplaces && t.id !== turnId), 1, mine);
          writeConv({ landingAgentTurns: next });
        }
      }
      if (opts?.defaultChange && evt.type === 'mission_error' && (evt as any).code === 'default_unavailable') {
        // #1808: the change can no longer be made — the answer stays as it was, its line says so, nothing else shows
        const notice = (evt as any).error || 'That choice is no longer available.';
        const turns = readConv().turns.filter((t) => t.id !== turnId)
          .map((t) => (t.sessionId === opts.defaultChange!.session ? { ...t, defaultsNotice: notice } : t));
        writeConv({ landingAgentTurns: turns }, true);
        persistThread();
        return;
      }
      if (evt.type === 'answer_delta') {
        // C8 (#1672): a streamed word lands on the turn's `streaming`, never in its events or the snapshot
        const streaming = reduceAnswerDelta(owningTurn, evt);
        if (streaming) writeConv({ landingAgentTurns: readConv().turns.map(t => t.id === turnId ? { ...t, streaming } : t) });
        return;
      }
      console.log('[agent-ws] event', evt.type, evt);
      if (evt.type === 'ladder' && myThreadGen !== threadGen(threadId)) {
        // A superseded socket's trailing envelope: a newer question owns the
        // thread now. The envelope belongs to ITS turn (found by its session)
        // and says nothing about the thread's scope any more.
        const sid = evt.ladder?.trace_session_id;
        const owned = readConv().turns;
        const idx = owned.findIndex((t) => t.id === turnId || (!!sid && t.sessionId === sid));
        if (idx >= 0 && (owned[idx].result?.ladder?.delivery?.revision || 0) <= (evt.ladder?.delivery?.revision || 0)) {
          writeConv({ landingAgentTurns: owned.map((t, i) => (i === idx ? { ...t, analysisAwaitingLadder: false, result: { ...(t.result || {}), ladder: evt.ladder } } : t)) });
        }
        persistThread();
        return;
      }
      const reduced = reduceTurnEvent(readConv(), owningTurn, evt, (ctx) => (
        // unless the screen moved to another perspective while this answer was in flight: a
        // late answer must not hand the new perspective the old one's metrics
        !onScreen() || !ctx.perspective || ctx.perspective === get().selectedPerspectiveId || get().selectedPerspectiveId === 'auto'));
      if (!reduced) return;
      const { patch, turnPatch } = reduced;
      // Apply turn patch to the last (in-flight) turn
      const turns = readConv().turns;
      if (turns.some((t: LandingAgentTurn) => t.id === turnId)) {
        const lastIdx = turns.findIndex((t: LandingAgentTurn) => t.id === turnId);
        const mergedTurn: LandingAgentTurn = { ...turns[lastIdx], ...turnPatch };
        patch.landingAgentTurns = turns.map((t, i) => (i === lastIdx ? mergedTurn : t));
      }
      const terminal = evt.type === 'mission_complete' || evt.type === 'diagnose_result'
        || evt.type === 'mission_error' || evt.type === 'mission_cancelled';
      writeConv(patch, terminal);

      const interactive = evt.type === 'clarification_required' || evt.type === 'definition_required';
      if (terminal || interactive || evt.type === 'ladder' || evt.type === 'mission_planned' || evt.type === 'analysis_started') {
        // Every terminal event, not only the happy ones: an error or a cancel
        // must reach the snapshot as well, or a reload restores a turn still
        // "running". The ladder envelope arrives after the terminal event, so
        // it persists the turn again.
        persistThread();
      }
    };

    // A handshake the backend rejects (expired Clerk session token → 4401)
    // delivers nothing. The token cache can only be READ at connect time,
    // never awaited, so reconnecting once with a freshly minted token is what
    // makes a stale one recoverable.
    let retried = false;

    const connect = () => {
      // The token refresh between attempts is asynchronous, and the store
      // holds no socket while it runs. If the user asked something else in
      // THIS thread meanwhile, this attempt belongs to nobody.
      if (myThreadGen !== threadGen(threadId) || readConv().turns.find(t => t.id === turnId)?.stopped) return;
      const url = wsUrl();
      console.log('[agent-ws] connecting', url, { perspective_id: askedRequest.perspective_id, mode: askedRequest.mode });
      const ws = new WebSocket(url);
      backgroundSockets.set(newTurn.analysisOperationId!, ws);
      writeConv({ landingAgentWs: ws });

      ws.onopen = () => {
        const owned = readConv().turns.find(t => t.id === turnId);
        const payload = {
          operation_id: newTurn.analysisOperationId,
          resume: !!resumed || progressed,
          after: owned?.analysisSequence || 0,
          ...askedRequest,
          // Change on the route chip (#760): re-ask under the route the asker chose, and say which it replaces.
          route: opts?.route,
          route_from: opts?.routeFrom,
          // a changed pick (#767), named beside the request like the Change, never kept on it
          ...(scopeFrom && scopeFor ? { scope_from: scopeFrom, scope_for: scopeFor } : {}),
          // A choice on a prior-work offer (#762): Start fresh or a Refresh, beside the request, never kept on it.
          prior: opts?.prior,
          // An answer to a clarifying question (#1155): the clarifying turn's session, beside the request, never kept on it.
          answers: opts?.answers,
          // A choice on a reading card (#1174 P3b): the card's session and an option id, beside the request, never kept on it.
          choice: opts?.choice || undefined,
          // #1807 (F2): which earlier answer the question means (or "none": Not that one), and the turn a Change
          // replaces — each beside the request, never kept on it
          earlier: opts?.earlier,
          replaces: opts?.replaces || undefined,
          // #1808 (F3): a one-click change of a stated default — references only, beside the request, never kept on it
          default_change: opts?.defaultChange || undefined,
        };
        console.log('[agent-ws] open → sending', payload);
        ws.send(JSON.stringify(payload));
        (ws as any).__voyagerSent = true;
      };

      ws.onmessage = handleMessage;

      ws.onerror = (e) => {
        if ((ws as any).__voyagerAbandoned) return;
        console.error('[agent-ws] error', e);
        // A socket that never delivered anything is left to onclose, which is
        // the only place that can tell a rejected token from a dead server —
        // and the only one that gets to retry.
        if (progressed) writeConv({ landingAgentTurns: readConv().turns.map(t => t.id === turnId && (t.status === 'running' || t.analysisAwaitingLadder) ? { ...t, analysisDisconnected: true } : t) });
      };

      ws.onclose = (e) => {
        if (backgroundSockets.get(newTurn.analysisOperationId!) === ws) backgroundSockets.delete(newTurn.analysisOperationId!);
        console.log('[agent-ws] close', { code: e.code, reason: e.reason, wasClean: e.wasClean });
        // Closed by us to make way for a new question, or by resetLandingAgent
        // — the socket that replaced this one owns the state now.
        if ((ws as any).__voyagerAbandoned) return;
        writeConv({ landingAgentWs: null });
        if ((ws as any).__voyagerSent && !e.wasClean && e.code !== WS_AUTH_REJECTED_CODE) mayBeRunning = true;
        if (progressed) {
          const owned = readConv().turns.find(t => t.id === turnId);
          if (owned?.status === 'running' || owned?.analysisAwaitingLadder) {
            writeConv({ landingAgentTurns: readConv().turns.map(t => t.id === turnId ? { ...t, analysisDisconnected: true } : t) });
            persistThread();
          } else finishTurn();
          return;
        }

        // Nothing ever came back over this socket, so there is no partial
        // answer to protect and one reconnect is always worth trying. Keyed on
        // silence rather than on `opened`, because the backend accepts a
        // rejected handshake before closing it and `opened` is therefore true
        // for exactly the case the retry exists to fix.
        if (myThreadGen !== threadGen(threadId)) return;
        if (!retried) {
          retried = true;
          getClerkToken().then(connect, connect);
          return;
        }
        failTurn(e.code === WS_AUTH_REJECTED_CODE
          ? 'Your session expired — please reload and ask again'
          : 'Could not reach the agent — please try again');
      };
    };

    connect();
    return true;   // the question started
  },

  stopBackgroundAnalysis: async (turnId) => {
    const state = get();
    const turn = state.landingAgentTurns.find(t => t.id === turnId);
    if (!turn?.analysisOperationId) return;
    const generation = state.threadsGeneration;
    const threadId = state.currentThreadId;
    const socket = backgroundSockets.get(turn.analysisOperationId);
    if (socket) {
      (socket as any).__voyagerAbandoned = true;
      socket.close();
      backgroundSockets.delete(turn.analysisOperationId);
    }
    const foreground = state.landingAgentTurns.find(t => t.foreground) || state.landingAgentTurns[state.landingAgentTurns.length - 1];
    // C8 (#1672): the words the card showed so far stay on it, each part's intact prefix, under the Stopped line
    const stoppedWords = stoppedWordsOf(turn.streaming);
    set({ landingAgentTurns: state.landingAgentTurns.map(t => t.id === turnId ? { ...withoutStreaming(t), stopped: true, status: 'cancelled', analysisCancelPending: true,
      ...(stoppedWords ? { stoppedWords } : {}) } : t),
      ...(foreground?.id === turnId ? { landingAgentStatus: 'cancelled' as const } : {}) });
    get().persistCurrentThread();
    try {
      const headers = await authHeaders();
      // The Stop belongs to the workspace it was pressed in: after a switch it stays pending and is retried there.
      if (get().threadsGeneration !== generation || get().currentThreadId !== threadId) return;
      const response = await fetch(`${process.env.REACT_APP_API_BASE || ''}/api/agent/runs/${encodeURIComponent(turn.analysisOperationId)}/cancel`, {
        method: 'POST', headers,
      });
      if (!response.ok) throw new Error('Stop could not be confirmed.');
      const outcome = (await response.json().catch(() => null))?.data;
      if (get().threadsGeneration !== generation || get().currentThreadId !== threadId) return;
      if (outcome?.cancelled === false || outcome?.status === 'done' || outcome?.status === 'failed') {
        // Its answer was stored before the Stop arrived: the answer stands — reconnect and replay it.
        const now = get();
        const front = now.landingAgentTurns.find(t => t.foreground) || now.landingAgentTurns[now.landingAgentTurns.length - 1];
        set({ landingAgentTurns: now.landingAgentTurns.map(t => t.id === turnId ? { ...t, stopped: false, status: 'running' as const, analysisCancelPending: false, analysisDisconnected: false } : t),
          ...(front?.id === turnId ? { landingAgentStatus: 'running' as const } : {}) });
        get().persistCurrentThread();
        get().sendLandingAgentQuery(turn.userText, { resumeTurnId: turnId });
        return;
      }
      set({ landingAgentTurns: get().landingAgentTurns.map(t => t.id === turnId ? { ...t, analysisCancelPending: false, analysisDisconnected: false } : t) });
      get().persistCurrentThread();
    } catch {
      if (get().threadsGeneration !== generation || get().currentThreadId !== threadId) return;
      set({ landingAgentTurns: get().landingAgentTurns.map(t => t.id === turnId ? { ...t, analysisDisconnected: true } : t) });
      get().persistCurrentThread();
    }
  },

  cancelLandingAgent: () => {
    const state = get();
    const foreground = state.landingAgentTurns.find(t => t.foreground) || state.landingAgentTurns[state.landingAgentTurns.length - 1];
    if (foreground?.analysisOperationId) {
      void get().stopBackgroundAnalysis(foreground.id);
      return;
    }
    // Stop has to reach a reconnect that is still waiting on a token refresh,
    // not just the socket. During that window there is no socket to send
    // 'cancel' to, and without this the question the user just stopped would
    // reconnect and run — spending agent work and answering after the fact.
    _attemptGen += 1;
    bumpThreadGen((get() as any).currentThreadId);
    const { landingAgentWs } = get();
    // A socket a background question still owns (kept on screen while a Why runs) is that question's to stop, not this Stop's.
    const ownedElsewhere = !!landingAgentWs && Array.from(backgroundSockets.values()).includes(landingAgentWs);
    if (landingAgentWs && !ownedElsewhere && landingAgentWs.readyState === WebSocket.OPEN) {
      try { landingAgentWs.send(JSON.stringify({ type: 'cancel' })); } catch {}
    }
    set({ landingAgentStatus: 'cancelled' });
    const turns = get().landingAgentTurns;
    if (turns.length) {
      // A re-run of an older card owns Stop while earlier diagnoses keep running.
      const foregroundIdx = turns.findIndex((t) => t.foreground);
      const stopIdx = foregroundIdx >= 0 ? foregroundIdx : turns.length - 1;
      set({ landingAgentTurns: turns.map((t, i) => (
        i === stopIdx ? { ...t, status: 'cancelled' as const, stopped: true, diagnosisCancelPending: !!t.diagnosisOperationId && t.status === 'running' } : t)) });
      // The turn was stored as running when it was asked; store the stop too,
      // or a reload shows a spinner over a run nobody is waiting for.
      const persist = (get() as any).persistCurrentThread;
      if (typeof persist === 'function') persist();
    }
  },

  resetLandingAgent: () => {
    const { landingAgentWs } = get();
    detachBackgroundSockets(get().landingAgentTurns);
    closeAgentWs(landingAgentWs, (get() as any).currentThreadId);
    set({
      landingAgentMessages: [],
      landingAgentHistory: [],
      landingAgentTurns: [],
      landingAgentEvents: [],
      landingAgentStatus: 'idle',
      landingAgentResult: null,
      landingAgentClarification: null,
      landingAgentDefinitionRequest: null,
      landingAgentError: null,
      landingAgentWs: null,
      landingAgentThreadContext: null,
    });
  },

  parkConversation: () => {
    const st = get();
    const threadId = (st as any).currentThreadId as string | null;
    if (threadId && (st.landingAgentTurns.length > 0 || st.landingAgentWs)) {
      // F4 (#1858 §5): leaving a running turn is what "while you were away" means; it keeps the mark when reopened
      const parked: ParkedConversation = {
        turns: st.landingAgentTurns.map((t) => {
          if (t.status !== 'running') return t;
          liveThisPage.delete(t.id);
          return { ...t, wasAway: true };
        }),
        history: st.landingAgentHistory,
        messages: st.landingAgentMessages,
        events: st.landingAgentEvents,
        status: st.landingAgentStatus,
        result: st.landingAgentResult,
        clarification: st.landingAgentClarification,
        definitionRequest: st.landingAgentDefinitionRequest,
        error: st.landingAgentError,
        ws: st.landingAgentWs,
        unread: false,
        threadContext: st.landingAgentThreadContext,
      };
      set({ parkedConversations: { ...st.parkedConversations, [threadId]: parked } });
    }
    // The screen clears; the socket, if any, is now the parked copy's.
    set({
      landingAgentMessages: [],
      landingAgentHistory: [],
      landingAgentTurns: [],
      landingAgentEvents: [],
      landingAgentStatus: 'idle',
      landingAgentResult: null,
      landingAgentClarification: null,
      landingAgentDefinitionRequest: null,
      landingAgentError: null,
      landingAgentWs: null,
      landingAgentThreadContext: null,
    });
  },

  unparkConversation: (threadId: string) => {
    const parked = get().parkedConversations[threadId];
    if (!parked) return false;
    const rest = { ...get().parkedConversations };
    delete rest[threadId];
    set({
      parkedConversations: rest,
      landingAgentTurns: parked.turns,
      landingAgentHistory: parked.history,
      landingAgentMessages: parked.messages,
      landingAgentEvents: parked.events,
      landingAgentStatus: parked.status,
      landingAgentResult: parked.result,
      landingAgentClarification: parked.clarification,
      landingAgentDefinitionRequest: parked.definitionRequest,
      landingAgentError: parked.error,
      landingAgentWs: parked.ws,
      landingAgentThreadContext: parked.threadContext ?? null,
    });
    return true;
  },

  dropParkedConversation: (threadId: string) => {
    const parked = get().parkedConversations[threadId];
    if (!parked) return;
    const lastIdx = parked.turns.length - 1;
    const lastStatus = lastIdx >= 0 ? parked.turns[lastIdx].status : null;
    // A run in flight is the last turn — or an older card being dug again (#184).
    const anyRunning = parked.turns.some((t) => t.status === 'running' && !t.analysisOperationId && !t.diagnosisOperationId);
    if (anyRunning || lastStatus === 'clarifying' || lastStatus === 'defining') {
      // The tab was closed on a run in flight: tell the server to stop,
      // settle the turn as cancelled, and store that — a reopen from
      // History must not show a spinner over a run nobody is waiting for.
      if (parked.ws && parked.ws.readyState === WebSocket.OPEN) {
        try { parked.ws.send(JSON.stringify({ type: 'cancel' })); } catch {}
      }
      const settled: ParkedConversation = {
        ...parked,
        status: 'cancelled',
        turns: parked.turns.map((t, i) => (
          (anyRunning ? t.status === 'running' && !t.analysisOperationId && !t.diagnosisOperationId : i === lastIdx)
            ? { ...t, status: 'cancelled' as const, stopped: true, diagnosisCancelPending: !!t.diagnosisOperationId && t.status === 'running' } : t)),
      };
      set({ parkedConversations: { ...get().parkedConversations, [threadId]: settled } });
      const persist = (get() as any).persistCurrentThread;
      if (typeof persist === 'function') persist(threadId);
    }
    detachBackgroundSockets(parked.turns);
    closeAgentWs(parked.ws, threadId);
    const rest = { ...get().parkedConversations };
    delete rest[threadId];
    set({ parkedConversations: rest });
  },

  discardParkedConversations: () => {
    for (const [threadId, parked] of Object.entries(get().parkedConversations)) {
      if (parked.ws && parked.ws.readyState === WebSocket.OPEN && !Array.from(backgroundSockets.values()).includes(parked.ws)) {
        try { parked.ws.send(JSON.stringify({ type: 'cancel' })); } catch {}
      }
      detachBackgroundSockets(parked.turns);
      closeAgentWs(parked.ws, threadId);
    }
    set({ parkedConversations: {} });
  },

  // Rehydrate one past answer, so a link out of Slack lands on the answer
  // rather than the home page. Read-only: it replays what was stored and does
  // not re-run the pipeline, so opening a link costs nothing and cannot
  // produce a different number than the one Slack showed.
  askWhyForClaim: (claim, subject, source, opts) => {
    const intent = opts?.intent === 'next' ? 'next' : 'why';
    // WHAT NEXT from a number is the same walk, read from its last rung: the
    // suggestions rest on the drivers, so the walk runs either way — never a
    // question in words to whatever perspective happens to be selected.
    const question = intent === 'next' ? `What should we do about ${subject}?` : `Why did ${subject} change?`;
    // The number's own perspective is the fact; the walk's session records it so a replay selects it again.
    const perspective_id = claim?.grounding?.perspective || get().selectedPerspectiveId || null;
    leaveReviewOnScreen(get, set, source);
    // The walk is asked in the number's perspective; the thread's pick stays the asker's (#767: never switched silently).
    // A walk opened from a number makes that number the thread's scope
    // (#189): a follow-up asked before the walk lands inherits its metric,
    // the row's filters and its window — not the previous answer's.
    const seed = contextFromClaim(claim);
    if (seed) set({ landingAgentThreadContext: seed });
    // #1818: the number names the answer it came from, so the walk is pinned to that answer's readings (the server
    // reads them from the asker's own session; never from the client)
    const asked = claim && !claim.session_id ? claimSession(get().landingAgentTurns, claim) : null;
    const posted = asked ? { ...claim, session_id: asked } : claim;
    runWhyTurn(get, set, question, (threadId, options) => runDiagnoseForClaim(posted, { thread_id: threadId, question, perspective_id }, options), undefined, perspective_id, intent);
  },

  correctWhyTurn: (turnId, saved) => {
    const turns = get().landingAgentTurns;
    const turn = turns.find((t) => t.id === turnId);
    if (!turn?.result?.diagnose) return;
    const diagnose = layerCorrection(turn.result.diagnose, saved as any);
    const result: LandingAgentResult = { ...turn.result, diagnose, synthesis: { ...(turn.result.synthesis || {}), headline: diagnose.verdict.headline } };
    set({
      landingAgentTurns: turns.map((t) => (t.id === turnId ? { ...t, result } : t)),
      ...(get().landingAgentResult?.diagnose === turn.result.diagnose ? { landingAgentResult: result } : {}),
    });
    const persist = (get() as any).persistCurrentThread;
    if (typeof persist === 'function') persist((get() as any).currentThreadId);
  },

  contributeWhyTurn: (turnId, saved) => {
    const turns = get().landingAgentTurns;
    const turn = turns.find((t) => t.id === turnId);
    if (!turn?.result?.diagnose) return;
    const diagnose = layerContribution(turn.result.diagnose, saved as any);
    const result: LandingAgentResult = { ...turn.result, diagnose };
    set({
      landingAgentTurns: turns.map((t) => (t.id === turnId ? { ...t, result } : t)),
      ...(get().landingAgentResult?.diagnose === turn.result.diagnose ? { landingAgentResult: result } : {}),
    });
    const persist = (get() as any).persistCurrentThread;
    if (typeof persist === 'function') persist((get() as any).currentThreadId);
  },

  raiseBar: (turnId, bar) => {
    const turn = get().landingAgentTurns.find((t) => t.id === turnId);
    const target = turn?.result?.diagnose?.target;
    if (!turn || !target?.metric) return;
    const perspective_id = turn.perspectiveId || get().selectedPerspectiveId || null;
    const effort = turn.result?.diagnose?.budget?.effort || 'quick';
    runWhyTurn(get, set, turn.userText, (threadId, options) => runDiagnose({
      metric: target.metric, window_start: target.window_start, window_end: target.window_end,
      filters: target.filters, effort, thread_id: threadId, question: turn.userText, perspective_id, bar: bar as any,
    }, options), turnId);
  },

  digDeeper: (turnId) => {
    const turn = get().landingAgentTurns.find((t) => t.id === turnId);
    const target = turn?.result?.diagnose?.target;
    if (!turn || turn.status === 'running' || !target?.metric || get().landingAgentTurns.some(t =>
      t.diagnosisParentTurnId === turnId && (t.status === 'running' || t.diagnosisCancelPending || t.diagnosisDisconnected))) return;
    const perspective_id = turn.perspectiveId || get().selectedPerspectiveId || null;
    const question = `Deeper analysis of ${(target.label || metricLabel(target.metric))}`;
    // Only an answer whose session belongs to the thread on screen can be continued (the server requires the same
    // thread); an answer opened from a link keeps its own session's thread, so here it gets a fresh deep walk instead
    // of a request that cannot succeed, even after a follow-up has started a thread.
    const currentThread = (get() as any).currentThreadId as string | null;
    const inThread = !!currentThread && (turn.sessionThreadId === undefined || turn.sessionThreadId === currentThread);
    runWhyTurn(get, set, question, (threadId, options) => runDiagnose({
      metric: target.metric, window_start: target.window_start, window_end: target.maturity?.original_window_end || target.window_end,
      continue_session_id: inThread ? (turn.sessionId || turn.result?.diagnose?.session_id) : undefined,
      filters: target.filters, effort: 'deep', thread_id: threadId, question, perspective_id,
    }, options), undefined, perspective_id, turn.intent, false, turnId);
  },

  retryWalk: (turnId) => {
    const turn = get().landingAgentTurns.find((t) => t.id === turnId);
    const target = turn?.result?.diagnose?.target;
    if (!turn || turn.status === 'running' || !target?.metric) return false;
    const perspective_id = turn.perspectiveId || get().selectedPerspectiveId || null;
    const effort = turn.result?.diagnose?.budget?.effort || 'quick';
    runWhyTurn(get, set, turn.userText, (threadId, options) => runDiagnose({
      metric: target.metric, window_start: target.window_start, window_end: target.maturity?.original_window_end || target.window_end,
      filters: target.filters, effort, thread_id: threadId, question: turn.userText, perspective_id,
    }, options), undefined, perspective_id, turn.intent);
    return true;
  },

  stopDiagnosis: (turnId) => {
    const turns = get().landingAgentTurns;
    const turn = turns.find(t => t.id === turnId);
    if (!turn?.diagnosisOperationId || turn.status !== 'running') return;
    const foreground = turns.find(t => t.foreground) || turns[turns.length - 1];
    set({ landingAgentTurns: turns.map(t => t.id === turnId ? { ...t, status: 'cancelled', stopped: true, diagnosisCancelPending: true } : t),
      ...(foreground?.id === turnId ? { landingAgentStatus: 'cancelled' as const } : {}) });
    get().persistCurrentThread();
  },

  resumeDiagnosis: (turnId) => {
    const turn = get().landingAgentTurns.find(t => t.id === turnId);
    if (!turn?.diagnosisOperationId || (!turn.diagnosisCancelPending && (turn.stopped || (turn.status !== 'running' && !turn.diagnosisDisconnected)))) return;
    runWhyTurn(get, set, turn.userText, (_threadId, options) => trackDiagnosis(options), turnId, turn.perspectiveId, turn.intent, true);
  },

  recoverLandingAgentAnswer: async (turnId, sessionId, signal) => {
    const { currentThreadId: threadId, threadsGeneration: generation } = get();
    const base = process.env.REACT_APP_API_BASE || '';
    try {
      const response = await fetch(`${base}/api/agent/session/${encodeURIComponent(sessionId)}`, {
        headers: { ...(await authHeaders()) }, signal,
      });
      if (response.status === 403 || response.status === 404) return 'not_found';
      if (!response.ok) return 'failed';
      const data = (await response.json())?.data;
      const ladder = data?.ladder;
      if (!ladder || data.session_id !== sessionId) return 'failed';
      const state = get();
      if (signal.aborted || state.currentThreadId !== threadId || state.threadsGeneration !== generation) return 'failed';
      const turn = state.landingAgentTurns.find((t) => t.id === turnId && t.sessionId === sessionId);
      if (!turn || turn.status !== 'done') return 'failed';
      const current = turn.result?.ladder;
      if ((current?.delivery?.revision || 0) > (ladder.delivery?.revision || 0)) return 'done';
      if (current?.delivery?.context_status === 'pending' && ladder.delivery?.context_status === 'pending'
        && current.delivery.revision === ladder.delivery.revision) return 'pending';
      const result = { ...turn.result, ladder };
      const latest = state.landingAgentTurns[state.landingAgentTurns.length - 1]?.id === turnId;
      set({ landingAgentTurns: state.landingAgentTurns.map((t) => t.id === turnId ? { ...t, result } : t),
        ...(latest ? { landingAgentResult: result,
          ...(ladder.thread_context && (!ladder.thread_context.perspective || ladder.thread_context.perspective === state.selectedPerspectiveId || state.selectedPerspectiveId === 'auto')
            ? { landingAgentThreadContext: ladder.thread_context } : {}) } : {}) });
      if (threadId) get().persistCurrentThread(threadId);
      return ladder.delivery?.context_status === 'pending' ? 'pending' : 'done';
    } catch { return 'failed'; }
  },

  restoreLandingAgentSession: async (sessionId: string, signal?: AbortSignal) => {
    const base = process.env.REACT_APP_API_BASE || '';
    try {
      const resp = await fetch(`${base}/api/agent/session/${encodeURIComponent(sessionId)}`, {
        headers: { ...(await authHeaders()) },
        signal,
      });
      if (!resp.ok) return false;
      const body = await resp.json();
      const d = body?.data;
      // only a landing answer has frames (#760): another session (an agent run, a workflow) is no turn to paint
      if (!d || !Array.isArray(d.events)) return false;

      // The answer as it was live (#760): its frames, replayed by the server as the socket sent them, painted
      // through the socket's own reducer — never a hand-mapped copy of the payload.
      let turn: LandingAgentTurn = {
        id: generateId(),
        userText: d.user_query || d.mission || '(restored answer)',
        sentAt: Date.now(),
        status: 'running',
        result: null,
        events: [],
        clarification: null,
        definitionRequest: null,
        error: null,
        sessionId,
        sessionThreadId: d.thread_id ?? null,
        feedback: null,
        perspectiveId: d.perspective_id || null,
        // the ask as the server received it; a replay keeps no history (it quotes earlier answers, #296)
        askedRequest: d.ask ? { attachment: null, ...d.ask, history: [] } : null,
      };
      let conv: TurnConversation = { events: [], status: 'running', result: null, history: [] };
      for (const evt of (d.events || []) as LandingAgentEvent[]) {
        const reduced = reduceTurnEvent(conv, turn, evt, () => true);
        if (!reduced) continue;
        const { patch, turnPatch } = reduced;
        turn = { ...turn, ...turnPatch };
        conv = { events: patch.landingAgentEvents ?? conv.events, status: patch.landingAgentStatus ?? conv.status,
          result: patch.landingAgentResult !== undefined ? patch.landingAgentResult : conv.result,
          history: patch.landingAgentHistory ?? conv.history };
      }

      // Checked immediately before the write, not after: StrictMode mounts
      // twice, and a late first response landing after the user has moved on
      // would silently replace whatever they were looking at.
      if (signal?.aborted) return false;
      set({
        landingAgentTurns: [turn],
        landingAgentStatus: turn.status,
        landingAgentResult: turn.result,
        landingAgentError: turn.error,
        landingAgentClarification: turn.clarification,
        landingAgentDefinitionRequest: turn.definitionRequest,
        // the pick the answer was asked under (#767: Auto stays Auto), else the perspective it was answered in
        ...((d.ask?.perspective_id || d.perspective_id) ? { selectedPerspectiveId: d.ask?.perspective_id || d.perspective_id } : {}),
      } as Partial<VoyagerState>);
      return true;
    } catch {
      return false;
    }
  },

  changeDefault: (turnId, id, option) => {
    const turn = get().landingAgentTurns.find((t) => t.id === turnId);
    // the answer's block, or the reading question's (#1818) — the same contract, the same reference
    const asked = (turn?.clarification?.questions || []).find((q) => q.id === 'reading')?.defaults;
    const block = [(turn?.result?.ladder as any)?.defaults, asked]
      .find((b: any) => (b?.items || []).some((i: any) => i.id === id));
    const reask = turn?.askedRequest;
    if (!turn || !reask || !block || block.changeable !== true || typeof block.session !== 'string') return false;
    const item = (block.items || []).find((i: any) => i.id === id);
    if (!item || !(item.options || []).some((o: any) => o.option === option)) return false;
    // the client sends only the reference — never dates, columns or labels
    return get().sendLandingAgentQuery(reask.query, { reask, defaultChange: { session: block.session, default: id, option },
                                                      replaces: turn.sessionId || null });
  },

  chooseReading: (turnId, optionId) => {
    const turn = get().landingAgentTurns.find((t) => t.id === turnId);
    const card = turn?.readingConfirmation;
    if (!turn || !card || card.chosen !== undefined) return false;
    const option = card.options.find((o) => o.option_id === optionId);
    if (!option) return false;
    // spent before anything is sent: a second click (or a double click) finds the card chosen and sends nothing
    set({ landingAgentTurns: get().landingAgentTurns.map((t) => (t.id === turnId
      ? { ...t, readingConfirmation: { ...card, chosen: optionId } } : t)) });
    // the words are the transcript only (the server runs the stored question, in its stored perspective), and they are
    // fixed: nothing of the card — a label, a description, a reading line — enters the turn, the history or the wire
    const started = get().sendLandingAgentQuery(choiceText(card.kind, optionId),
      { ...followUpOf(turn), choice: { session: card.session_id, option_id: optionId } });
    if (!started) {
      set({ landingAgentTurns: get().landingAgentTurns.map((t) => (t.id === turnId ? { ...t, readingConfirmation: card } : t)) });
    } else get().resolveWait(turnId);
    return started;
  },

  submitTurnFeedback: async (turnId, feedback) => {
    const turns = get().landingAgentTurns;
    const turn = turns.find((t) => t.id === turnId);
    if (!turn) return;
    const updated = turns.map((t) =>
      t.id === turnId ? { ...t, feedback } : t,
    );
    set({ landingAgentTurns: updated });
    const threadId = (get() as any).currentThreadId as string | null | undefined;
    try {
      const base = process.env.REACT_APP_API_BASE || '';
      await fetch(`${base}/api/agent/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({
          session_id: turn.sessionId,
          thread_id: threadId || null,
          rating: feedback.rating,
          tags: feedback.tags || [],
          comment: feedback.comment || null,
          user_query: turn.userText,
          // feedback is kept by the server under ownership alone: a claim's words (#296) never ride it
          headline: historyText(turn.result?.synthesis?.headline || turn.result?.formatterTitle || '', turn.result) || null,
        }),
      });
    } catch (e) {
      console.warn('[feedback] submit failed', e);
    }
    const persist = (get() as any).persistCurrentThread;
    if (typeof persist === 'function') persist();
  },

  clearTurnFeedback: (turnId) => {
    const turns = get().landingAgentTurns;
    const updated = turns.map((t) =>
      t.id === turnId ? { ...t, feedback: null } : t,
    );
    set({ landingAgentTurns: updated });
  },
});
